import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { semanticColors, colorTokens } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { Button } from '@/common/components/button/Button';
import { Input } from '@/components/ui/Input';
import { toastUtils } from '@/common/utils/toastUtils';
import { commonService } from '@/services/commonService';
import { fileService } from '@/services/fileService';
import { aiService } from '@/services/aiService';
import { CheckExistCompanyResponse, companyService } from '@/services/companyService';
import { extractDate } from '@/common/utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import { useNavigationStore } from '@/common/store';
import { useAuthStore } from '@/store/useAuthStore';
import { authService } from '@/services/authService';
import FileUpload from '@/common/components/fileUpload/FileUpload';
import { 
  Rocket, 
  Banknote, 
  Lightbulb, 
  User, 
  Building2, 
  LayoutGrid, 
  Check,
  ChevronLeft,
  Plus,
  Loader2,
  Briefcase,
  Crown,
  UserX,
  CheckCircle2,
  Pencil,
  Sparkles,
  CreditCard,
  MessageSquare,
  RotateCcw,
  ArrowRight,
} from 'lucide-react';
import { companyUserService } from '@/services/companyUserService';
import { billingService } from '@/services/billingService';
import { spaceService } from '@/services/spaceService';
import { BillingProductItem } from '@/types/billing';
import { LoginCompanyUserInfo } from '@/common/types/login';

/**
 * 회사 등록 API 응답에 `insertedId`가 누락되는 경우를 대비해
 * 프로필의 `companyUsers` 목록에서 등록 시 입력한 `companyCode`(우선) 또는
 * 법인 등록번호 `businessNumber`로 매칭해 회사 ID를 복구한다.
 */
const resolveRegisteredCompanyId = (params: {
  insertedId?: number;
  companyCode?: string;
  businessNumber?: string;
  profileCompanies?: LoginCompanyUserInfo[];
}): number | null => {
  const { insertedId, companyCode, businessNumber, profileCompanies } = params;

  if (insertedId != null && !Number.isNaN(Number(insertedId))) {
    return Number(insertedId);
  }

  if (!profileCompanies || profileCompanies.length === 0) {
    return null;
  }

  const normalizedCode = companyCode?.trim().toLowerCase();
  if (normalizedCode) {
    const matchedByCode = profileCompanies.find(
      (cu) => cu.company?.code?.trim().toLowerCase() === normalizedCode,
    );
    if (matchedByCode?.company?.id != null) {
      return matchedByCode.company.id;
    }
  }

  const normalizedBizNo = businessNumber?.replace(/[^0-9]/g, '');
  if (normalizedBizNo) {
    const matchedByBizNo = profileCompanies.find(
      (cu) => cu.company?.businessNumber?.replace(/[^0-9]/g, '') === normalizedBizNo,
    );
    if (matchedByBizNo?.company?.id != null) {
      return matchedByBizNo.company.id;
    }
  }

  return null;
};

// 서비스 타입
type ServiceType = 'STARTUP' | 'INVESTOR' | 'ACMS';

// 창업자 타입 (스타트업만 예비 창업자 가능)
type FounderType = 'PRELIMINARY' | 'CORPORATE';

// 프로세스 단계
type ProcessStep = 'SERVICE_SELECT' | 'FOUNDER_TYPE' | 'FORM_INPUT' | 'BUSINESS_UPLOAD' | 'VERIFICATION' | 'PLAN_SELECT';

// AI 분석 결과
interface AIAnalysisResult {
  companyName: string;
  businessNumber: string;
  representative: string;
  corporateNumber: string;
  establishedDate: string;
}

// 회사 DB 조회 결과
interface CompanyDBResult {
  exists: boolean;
  hasActiveUser: boolean;
  companyId?: string;
}

// 폼 데이터
interface CompanyForm {
  // 공통
  serviceType: ServiceType;
  founderType: FounderType;
  companyEmail: string;
  companyCode: string;
  
  // 예비 창업자
  companyName?: string;
  isRepresentative?: boolean;
  ceoName?: string;
  ceoEmail?: string;
  
  // 법인 창업자
  businessFile?: File;
  businessFileId?: number;
  aiAnalysis?: AIAnalysisResult;
}

// 플랜 선택 결과 (결제 완료 화면)
type PlanResultType = 'FREE_SUBSCRIBED' | 'PAID_SUBSCRIBED' | 'CONSULTATION_SUBMITTED';

interface PlanResult {
  type: PlanResultType;
  planName: string;
  message?: string;
  amount?: number;
  eventType?: 'NONE' | 'CHARGE' | 'REFUND';
}

interface FormErrors {
  serviceType?: string;
  founderType?: string;
  companyName?: string;
  companyEmail?: string;
  companyCode?: string;
  ceoName?: string;
  ceoEmail?: string;
  businessFile?: string;
  businessNumber?: string;
  representative?: string;
  corporateNumber?: string;
  establishedDate?: string;
}

const NewCompanyRegistration: React.FC = () => {
  const navigate = useNavigate();
  const {   setHeaderTitle, setBackNavigation, clearBackNavigation } = useNavigationStore();
  const { user } = useAuthStore();
  const [currentStep, setCurrentStep] = useState<ProcessStep>('SERVICE_SELECT');
  const [formData, setFormData] = useState<CompanyForm>({
    serviceType: 'STARTUP',
    founderType: 'PRELIMINARY',
    companyEmail: '',
    companyCode: '',
  });

  const [verificationBusinessInfo, setVerificationBusinessInfo] = useState<"READY" | "EMPTY_DATA" | "FAILED" | "EXIST_COMPANY_EXIST_MASTER" | "EXIST_COMPANY_NOT_EXIST_MASTER">("READY");  
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'JOIN_REQUEST' | 'MASTER_MATCH' | null>(null);
  const [companyDBResult, setCompanyDBResult] = useState<CompanyDBResult | null>(null);
  const [existingCompanyInfo, setExistingCompanyInfo] = useState<CheckExistCompanyResponse["company"] | undefined>(undefined);
  const [isCodeChecked, setIsCodeChecked] = useState(false);
  const [isCheckingCode, setIsCheckingCode] = useState(false);
  const [isEmailChecked, setIsEmailChecked] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [joinRequestEmail, setJoinRequestEmail] = useState<string>('');
  const [masterCompanyEmail, setMasterCompanyEmail] = useState<string>('');
  const [masterCompanyCode, setMasterCompanyCode] = useState<string>('');
  const [isMasterEmailChecked, setIsMasterEmailChecked] = useState(false);
  const [isMasterCodeChecked, setIsMasterCodeChecked] = useState(false);
  const [isCheckingMasterEmail, setIsCheckingMasterEmail] = useState(false);
  const [isCheckingMasterCode, setIsCheckingMasterCode] = useState(false);
  const [isJoinEmailChecked, setIsJoinEmailChecked] = useState(false);
  const [isCheckingJoinEmail, setIsCheckingJoinEmail] = useState(false);

  // 빌링 관련 상태 (INVESTOR 플랜 선택)
  const [registeredCompanyId, setRegisteredCompanyId] = useState<number | null>(null);
  const [plans, setPlans] = useState<BillingProductItem[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [isPlanProcessing, setIsPlanProcessing] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [planListRefreshToken, setPlanListRefreshToken] = useState(0);
  const [planResult, setPlanResult] = useState<PlanResult | null>(null);

  // 도입 문의 모달 상태
  const [consultationModal, setConsultationModal] = useState<{
    isOpen: boolean;
    productId: number | null;
    planName: string;
  }>({ isOpen: false, productId: null, planName: '' });
  const [consultationMessage, setConsultationMessage] = useState('');
  const [isSubmittingConsultation, setIsSubmittingConsultation] = useState(false);
  const cardPopupRef = useRef<Window | null>(null);
  const cardPopupWatcherRef = useRef<number | null>(null);

  /**
   * 서비스 타입 정의
   */
  const serviceTypes: {
    type: ServiceType;
    name: string;
    icon: React.ReactNode;
    description: string;
    features: string[];
    isPreparing: boolean;
  }[] = [
    {
      type: 'STARTUP',
      name: '스타트업 공간',
      icon: <Rocket size={24} />,
      description: '스타트업을 위한 투자 유치, 사업 관리 서비스',
      features: ['투자 유치 관리', '사업 계획서 관리', '투자자 연결', '성장 지원'],
      isPreparing: false,
    },
    {
      type: 'INVESTOR',
      name: '투자자 공간',
      icon: <Banknote size={24} />,
      description: '투자 관리 및 딜 소싱 전문 서비스',
      features: ['포트폴리오 관리', '딜 소싱', '투자 분석', '리포트 생성'],
      isPreparing: false,
    },
    {
      type: 'ACMS',
      name: 'ACMS 공간',
      icon: <Lightbulb size={24} />,
      description: '액셀러레이터 및 프로그램 운영 서비스',
      features: ['프로그램 운영', '참가자 관리', '평가 시스템', '성과 분석'],
      isPreparing: true,
    },
  ];

  /**
   * 현재 스텝 번호 계산
   */
  const getCurrentStepNumber = () => {
    switch (currentStep) {
      case 'SERVICE_SELECT': return 1;
      case 'FOUNDER_TYPE': return 2;
      case 'FORM_INPUT':
      case 'BUSINESS_UPLOAD':
      case 'VERIFICATION': return 3;
      case 'PLAN_SELECT': return 4;
      default: return 1;
    }
  };

  const getTotalSteps = () => {
    return formData.serviceType === 'INVESTOR' ? 4 : 3;
  };

  /**
   * 서비스 타입 선택 핸들러
   */
  const handleServiceSelect = (serviceType: ServiceType) => {
    setFormData(prev => ({
      ...prev,
      serviceType,
      // 스타트업이 아닌 경우 법인 창업자로 강제 설정
      founderType: serviceType === 'STARTUP' ? prev.founderType : 'CORPORATE',
    }));
    setCurrentStep('FOUNDER_TYPE');
  };

  /**
   * 창업자 타입 선택 핸들러
   */
  const handleFounderTypeSelect = (founderType: FounderType) => {
    setFormData(prev => ({
      ...prev,
      founderType,
    }));
    
    if (founderType === 'PRELIMINARY') {
      setCurrentStep('FORM_INPUT');
    } else {
      setCurrentStep('BUSINESS_UPLOAD');
    }
  };

  /**
   * 회사 코드 포맷팅 (영문 대문자 + 숫자, 최대 8자)
   */
  const formatCompanyCode = (value: string) => {
    return value.replace(/[^A-Za-z0-9]/g, '');
  };

  /**
   * 사업자등록번호 포맷팅 (000-00-00000)
   */
  const formatBusinessNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5, 10)}`;
  };

  /**
   * 법인등록번호 포맷팅 (123456-1234567)
   */
  const formatCorporateNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 6) return numbers;
    return `${numbers.slice(0, 6)}-${numbers.slice(6, 13)}`;
  };

  /**
   * 설립일 포맷팅 (YYYY-MM-DD)
   */
  const formatEstablishmentDate = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 4) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 4)}-${numbers.slice(4)}`;
    return `${numbers.slice(0, 4)}-${numbers.slice(4, 6)}-${numbers.slice(6, 8)}`;
  };

  /**
   * 회사 코드 유효성 검증
   */
  const validateCompanyCode = (code: string) => {
    if (!code.trim()) {
      return '회사 코드를 입력해주세요.';
    }
    if (code.length < 4) {
      return '회사 코드는 최소 4자 이상이어야 합니다.';
    }
    if (!/^[A-Za-z0-9]+$/.test(code)) {
      return '회사 코드는 영문과 숫자만 사용 가능합니다.';
    }
    return undefined;
  };

  /**
   * 이메일 유효성 검증
   */
  const validateEmail = (email: string) => {
    if (!email.trim()) {
      return '이메일을 입력해주세요.';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return '올바른 이메일 형식이 아닙니다.';
    }
    return undefined;
  };

  /**
   * 입력 필드 변경 핸들러
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // 회사 코드 특별 처리
    if (name === 'companyCode') {
      processedValue = formatCompanyCode(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue,
    }));
    
    // 실시간 유효성 검증
    if (name === 'companyCode') {
      const error = validateCompanyCode(processedValue);
      setErrors(prev => ({
        ...prev,
        companyCode: error,
      }));
      // 회사 코드가 변경되면 중복 확인 상태 초기화
      setIsCodeChecked(false);
    } else if (name === 'companyEmail') {
      const error = validateEmail(processedValue);
      setErrors(prev => ({
        ...prev,
        companyEmail: error,
      }));
      // 회사 이메일이 변경되면 중복 확인 상태 초기화
      setIsEmailChecked(false);
    } else {
      // 에러 메시지 초기화
      if (errors[name as keyof FormErrors]) {
        setErrors(prev => ({
          ...prev,
          [name]: undefined,
        }));
      }
    }
  };

  /**
   * 회사 코드 중복 확인
   */
  const handleCheckCompanyCode = async () => {
    const error = validateCompanyCode(formData.companyCode);
    if (error) {
      setErrors(prev => ({
        ...prev,
        companyCode: error,
      }));
      return;
    }

    // 이미 확인된 코드인지 체크
    if (isCodeChecked) {
      return;
    }

    setIsCheckingCode(true);
    
    try {
      
      const result = await commonService.checkDuplicateValue('companyCode', formData.companyCode);    
      
      if (result.exists) {
        setErrors(prev => ({
          ...prev,
          companyCode: '이미 사용 중인 회사 코드입니다.',
        }));
        setIsCodeChecked(false);
      } else {
        setErrors(prev => ({
          ...prev,
          companyCode: undefined,
        }));
        setIsCodeChecked(true);
        toastUtils.success('사용 가능한 회사 코드입니다.');
      }
      
    } catch (error) {
      toastUtils.error('회사 코드 확인 중 오류가 발생했습니다.');
    } finally {
      setIsCheckingCode(false);
    }
  };

  /**
   * 회사 이메일 중복 확인
   */
  const handleCheckCompanyEmail = async () => {
    const error = validateEmail(formData.companyEmail);
    if (error) {
      setErrors(prev => ({
        ...prev,
        companyEmail: error,
      }));
      return;
    }

    // 이미 확인된 이메일인지 체크
    if (isEmailChecked) {
      return;
    }

    setIsCheckingEmail(true);
    
    try {
      const result = await companyUserService.checkDuplicateEmail(formData.companyEmail);
      
      if (result.exists) {
        setErrors(prev => ({
          ...prev,
          companyEmail: '이미 사용 중인 이메일입니다.',
        }));
        setIsEmailChecked(false);
      } else {
        setErrors(prev => ({
          ...prev,
          companyEmail: undefined,
        }));
        setIsEmailChecked(true);
        toastUtils.success('사용 가능한 이메일입니다.');
      }
      
    } catch (error) {
      toastUtils.error('이메일 확인 중 오류가 발생했습니다.');
    } finally {
      setIsCheckingEmail(false);
    }
  };

  /**
   * 마스터 등록용 회사 이메일 중복 확인
   */
  const handleCheckMasterCompanyEmail = async () => {
    const error = validateEmail(masterCompanyEmail);
    if (error) {
      toastUtils.error(error);
      return;
    }

    // 이미 확인된 이메일인지 체크
    if (isMasterEmailChecked) {
      return;
    }

    setIsCheckingMasterEmail(true);
    
    try {
      const result = await commonService.checkDuplicateValue('email', masterCompanyEmail);
      
      if (result.exists) {
        toastUtils.error('이미 사용 중인 이메일입니다.');
        setIsMasterEmailChecked(false);
      } else {
        setIsMasterEmailChecked(true);
        toastUtils.success('사용 가능한 이메일입니다.');
      }
      
    } catch (error) {
      toastUtils.error('이메일 확인 중 오류가 발생했습니다.');
    } finally {
      setIsCheckingMasterEmail(false);
    }
  };

  /**
   * 마스터 등록용 회사 코드 중복 확인
   */
  const handleCheckMasterCompanyCode = async () => {
    const error = validateCompanyCode(masterCompanyCode);
    if (error) {
      toastUtils.error(error);
      return;
    }

    // 이미 확인된 코드인지 체크
    if (isMasterCodeChecked) {
      return;
    }

    setIsCheckingMasterCode(true);
    
    try {
      const result = await commonService.checkDuplicateValue('companyCode', masterCompanyCode);
      
      if (result.exists) {
        toastUtils.error('이미 사용 중인 회사 코드입니다.');
        setIsMasterCodeChecked(false);
      } else {
        setIsMasterCodeChecked(true);
        toastUtils.success('사용 가능한 회사 코드입니다.');
      }
      
    } catch (error) {
      toastUtils.error('회사 코드 확인 중 오류가 발생했습니다.');
    } finally {
      setIsCheckingMasterCode(false);
    }
  };

  /**
   * 직원 가입 신청용 이메일 중복 확인
   */
  const handleCheckJoinEmail = async () => {
    const error = validateEmail(joinRequestEmail);
    if (error) {
      toastUtils.error(error);
      return;
    }

    // 이미 확인된 이메일인지 체크
    if (isJoinEmailChecked) {
      return;
    }

    setIsCheckingJoinEmail(true);
    
    try {
      const result = await commonService.checkDuplicateValue('email', joinRequestEmail);
      
      if (result.exists) {
        toastUtils.error('이미 사용 중인 이메일입니다.');
        setIsJoinEmailChecked(false);
      } else {
        setIsJoinEmailChecked(true);
        toastUtils.success('사용 가능한 이메일입니다.');
      }
      
    } catch (error) {
      toastUtils.error('이메일 확인 중 오류가 발생했습니다.');
    } finally {
      setIsCheckingJoinEmail(false);
    }
  };

  /**
   * 사업자등록증 파일 업로드 핸들러
   */
  const handleFileUpload = (files: File[]) => {
    if (files.length > 0) {
      setUploadedFile(files[0]);
      setFormData(prev => ({
        ...prev,
        businessFile: files[0],
      }));
    } else {
      setUploadedFile(null);
      setFormData(prev => ({
        ...prev,
        businessFile: undefined,
        aiAnalysis: undefined,
      }));
    }
  };

  /**
   * AI 분석 및 인증 처리
   */
  const handleVerification = async () => {

    if (isVerifying) {
      return;
    }

    if (!uploadedFile) return;

    setIsVerifying(true);
    
    try {

      const fileResult = await fileService.uploadFile([uploadedFile]);

      if(!fileResult) return toastUtils.error('파일 업로드에 실패했습니다. 파일을 확인해주세요.');

      const result = await aiService.getAnalyzeBusinessRegistration({
        fileId: fileResult[0].id,
      });

      setFormData(prev => ({
        ...prev,
        businessFileId: fileResult[0].id,
      }));

      if (result) {
        const analysis: AIAnalysisResult = {
          companyName: result.companyName,
          businessNumber: result.businessNumber,
          representative: result.representative,
          corporateNumber: result.corporateNumber,
          establishedDate: result.establishmentDate,
        };
        setFormData(prev => ({
          ...prev,
          aiAnalysis: analysis,
        }));

        if(analysis.companyName && analysis.businessNumber && analysis.representative && analysis.corporateNumber && analysis.establishedDate) {
          await autoCheckCompanyStatus(analysis);          
        }else{
          setVerificationBusinessInfo("EMPTY_DATA");          
          toastUtils.error('사업자 정보 인증이 실패하였습니다. 법인등록번호 또는 설립일이 정확한지 확인해주세요.');
          return;
        }        
      } else {
        // AI 분석 실패 - 수동 입력 모드
        toastUtils.error('AI 분석이 완료되지 않았습니다. 정보를 직접 입력해주세요.');
        setVerificationBusinessInfo("EMPTY_DATA");
      }
      
    } catch (error) {
      toastUtils.error('사업자등록증 분석 중 오류가 발생했습니다.');
    } finally {
      setIsVerifying(false);
    }
  };


  const autoCheckCompanyStatus = async (data: AIAnalysisResult) => {
    try {
      console.log('autoCheckCompanyStatus', data);
      const representative = data.representative.split(",").map(name => name.trim());
      let isExistCompany = false;
      for(let i = 0; i < representative.length; i++) {
        const statusResponse = await companyService.checkCompanyStatus({
          companyName: data.companyName,
          ceoName: representative[i],
          businessNumber: data.businessNumber.replace(/-/g, ''),
          corporateNumber: data.corporateNumber.replace(/-/g, ''),
          establishedDate: extractDate(data.establishedDate).replace(/-/g, '')
        });

        if (statusResponse.isApiError) {
          toastUtils.error('국세청 api 오류로 지연되고 있습니다. 1시간 후에 다시 시도해 주세요');
          setVerificationBusinessInfo("FAILED");
          return;
        }

        if(statusResponse.isExistCompany) {
          isExistCompany = true;
          setFormData((prev: any) => ({
            ...prev,
            ceoName: representative[i],
          }));
          break;
        }
      }

       if(isExistCompany) {

        const existCompanyResponse = await companyService.checkExistCompany(data.businessNumber);
        setFormData((prev: any) => ({
          ...prev,
          businessNumber: data.businessNumber,
          corporateNumber: data.corporateNumber,
          foundingDate: extractDate(data.establishedDate),
          isBusinessVerified: true,
        }));
        
        if(existCompanyResponse.isExist) {
          // 기존 회사 정보 저장
          setExistingCompanyInfo(existCompanyResponse.company);
          
          if(existCompanyResponse.company!.masterUser !== undefined){
            setVerificationBusinessInfo("EXIST_COMPANY_EXIST_MASTER");
          } else {
            setVerificationBusinessInfo("EXIST_COMPANY_NOT_EXIST_MASTER");
          }
        } else {
          setCurrentStep('FORM_INPUT');
        }
       } else {
         toastUtils.error('사업자 정보 인증이 실패하였습니다. 법인등록번호 또는 설립일이 정확한지 확인해주세요.');
         setVerificationBusinessInfo("FAILED");
         return;
       }
    } catch (error) {
      console.error('자동 회사 상태 확인 중 오류:', error);
      setVerificationBusinessInfo("FAILED");
      return;
    }
  };

  /**
   * 수동 입력 정보 유효성 검증
   */
  const validateManualInput = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.aiAnalysis?.companyName?.trim()) {
      newErrors.companyName = '사업자명을 입력해주세요.';
    }
    
    if (!formData.aiAnalysis?.businessNumber?.trim()) {
      newErrors.businessNumber = '사업자등록번호를 입력해주세요.';
    } else if (!/^\d{3}-\d{2}-\d{5}$/.test(formData.aiAnalysis.businessNumber)) {
      newErrors.businessNumber = '올바른 사업자등록번호 형식이 아닙니다. (예: 123-45-67890)';
    }
    
    if (!formData.aiAnalysis?.representative?.trim()) {
      newErrors.representative = '대표자명을 입력해주세요.';
    }
    
    // if (!formData.aiAnalysis?.corporateNumber?.trim()) {
    //   newErrors.corporateNumber = '법인등록번호를 입력해주세요.';
    // } else if (!/^\d{6}-\d{7}$/.test(formData.aiAnalysis.corporateNumber)) {
    //   newErrors.corporateNumber = '올바른 법인등록번호 형식이 아닙니다. (예: 123456-1234567)';
    // }
    
    if (!formData.aiAnalysis?.establishedDate?.trim()) {
      newErrors.establishedDate = '설립일을 입력해주세요.';
    } else if (!/^\d{4}-\d{2}-\d{2}$/.test(formData.aiAnalysis.establishedDate)) {
      newErrors.establishedDate = '올바른 설립일 형식이 아닙니다. (예: 2020-01-15)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * 수동 입력 정보 인증 처리
   */
  const handleManualVerification = async () => {
    if (!validateManualInput()) {
      return;
    }

    if (!formData.aiAnalysis) return;

    setIsVerifying(true);
    
    try {
      await autoCheckCompanyStatus(formData.aiAnalysis);
    } catch (error) {
      console.error('수동 인증 중 오류:', error);
      toastUtils.error('사업자 정보 인증 중 오류가 발생했습니다.');
    } finally {
      setIsVerifying(false);
    }
  };

  /**
   * AI 분석 결과 확정 및 DB 조회
   */
  const handleVerifyAndCheck = async () => {
    if (!formData.aiAnalysis?.businessNumber) return;

    setIsLoading(true);
    
    try {
      const response: CheckExistCompanyResponse = await companyService.checkExistCompany(
        formData.aiAnalysis.businessNumber,
      );

      if (response.isExist && response.company) {
        const hasMaster = response.company.masterUser != null;
        setExistingCompanyInfo(response.company);
        setCompanyDBResult({ exists: true, hasActiveUser: hasMaster });

        if (hasMaster) {
          setModalType('JOIN_REQUEST');
        } else {
          setModalType('MASTER_MATCH');
        }
        setShowModal(true);
      } else {
        setCompanyDBResult({ exists: false, hasActiveUser: false });
        setCurrentStep('FORM_INPUT');
      }
      
    } catch (error) {
      toastUtils.error('회사 정보 확인 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 직원 가입 신청 핸들러
   */
  const handleEmployeeJoinRequest = async () => {
    if (!joinRequestEmail.trim()) {
      toastUtils.error('이메일을 입력해주세요.');
      return;
    }

    if (!isJoinEmailChecked) {
      toastUtils.error('이메일 중복 확인을 해주세요.');
      return;
    }

    // 이메일 형식 검증
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(joinRequestEmail)) {
      toastUtils.error('올바른 이메일 형식이 아닙니다.');
      return;
    }

    setIsLoading(true);
    
    try {
      // 가입 신청 데이터 준비
      const joinRequestData = {        
        companyId: existingCompanyInfo!.id,
        email: joinRequestEmail
      };

      console.log('직원 가입 신청 데이터:', joinRequestData);
      
      const result = await companyService.applyCompanyJoinRequest(joinRequestData);
      if(result.success) {
        toastUtils.success('직원 가입 신청이 완료되었습니다. 마스터 승인을 기다려주세요.');
        navigate('/notifications');
      } else {
        toastUtils.error(result.message || '직원 가입 신청 중 오류가 발생했습니다.');
      }
      
    } catch (error) {
      toastUtils.error('가입 신청 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 마스터 등록 핸들러
   */
  const handleMasterRegistration = async () => {
    if (!formData.businessFileId) {
      toastUtils.error('사업계획서를 업로드해주세요.');
      return;
    }

    // 회사 이메일 검증
    if (!masterCompanyEmail.trim()) {
      toastUtils.error('회사에서 사용할 이메일을 입력해주세요.');
      return;
    }

    if (!isMasterEmailChecked) {
      toastUtils.error('회사 이메일 중복 확인을 해주세요.');
      return;
    }

    // 회사 코드 검증
    if (!masterCompanyCode.trim()) {
      toastUtils.error('회사 코드를 입력해주세요.');
      return;
    }

    if (!isMasterCodeChecked) {
      toastUtils.error('회사 코드 중복 확인을 해주세요.');
      return;
    }

    // 이메일 형식 검증
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(masterCompanyEmail)) {
      toastUtils.error('올바른 회사 이메일 형식이 아닙니다.');
      return;
    }

    setIsLoading(true);
    
    try {
      // 마스터 등록 데이터 준비
      const masterRegistrationData = {
        serviceType: formData.serviceType,
        companyId: existingCompanyInfo!.id,
        email: masterCompanyEmail,
        companyCode: masterCompanyCode,
        businessPlanFileId: formData.businessFileId
      };

      console.log('마스터 등록 데이터:', masterRegistrationData);
      
      const result = await companyService.matchingCompany(masterRegistrationData);
      if(result.success) {
        await authService.setUserProfile();
        if (formData.serviceType === 'INVESTOR') {
          const linkedCompanyId = existingCompanyInfo?.id;
          if (linkedCompanyId == null || Number.isNaN(Number(linkedCompanyId))) {
            toastUtils.error(
              '회사 연결 정보를 확인할 수 없습니다. 관리자에게 문의해주세요.',
            );
            return;
          }
          setRegisteredCompanyId(linkedCompanyId);
          setCurrentStep('PLAN_SELECT');
        } else {
          toastUtils.success('마스터 등록이 완료되었습니다.');
          navigate('/space-management');
        }
      } else {
        toastUtils.error(result.message || '마스터 등록 중 오류가 발생했습니다.');
      }          
    } catch (error) {
      toastUtils.error('마스터 등록 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 대표자 이름 일치 여부 확인 (법인 창업자용)
   * 사업자등록증의 대표자와 로그인한 유저 이름을 비교
   */
  const checkIsRepresentative = (): boolean => {
    if (formData.founderType !== 'CORPORATE' || !formData.aiAnalysis?.representative || !user?.name) {
      return false;
    }
    
    // 대표자가 여러명인 경우 쉼표로 구분되어 있음
    const representatives = formData.aiAnalysis.representative
      .split(',')
      .map(name => name.trim());
    
    return representatives.some(rep => rep === user.name);
  };

  /**
   * 최종 제출 핸들러
   */
  const handleFinalSubmit = async () => {
    setIsLoading(true);
    
    try {
      console.log('handleFinalSubmit', formData);
      let bodyData;

      if(formData.founderType === 'PRELIMINARY') {
        bodyData = {
          serviceType: formData.serviceType,
          companyEmail: formData.companyEmail,
          companyCode: formData.companyCode,
          isPreStartup: true,
          isCeo: formData.isRepresentative,
          companyName: formData.aiAnalysis!.companyName,
          ceoName: formData.ceoName,
          ceoEmail: formData.ceoEmail,
        }
      }else{
        const isCorporateRepresentative = checkIsRepresentative();
        bodyData = {
          serviceType: formData.serviceType,
          companyEmail: formData.companyEmail,
          companyCode: formData.companyCode,
          isPreStartup: false,
          isCeo: isCorporateRepresentative,
          companyName: formData.aiAnalysis!.companyName,
          businessNumber: formData.aiAnalysis!.businessNumber,
          corporateNumber: formData.aiAnalysis?.corporateNumber || undefined,
          isBusinessVerified: true,
          foundingDate: formData.aiAnalysis!.establishedDate,
          ceoName: formData.aiAnalysis!.representative,
          businessRegistryFileId: formData.businessFileId
        }
      }

      const result = await companyService.registerCompany(bodyData);
      if(result.success) {
        // 서버 응답(insertedId) 우선, 누락 시 프로필/사업자번호로 복구
        const profileResult = await authService.setUserProfile();
        const resolvedCompanyId = resolveRegisteredCompanyId({
          insertedId: result.insertedId,
          companyCode: formData.companyCode,
          businessNumber:
            formData.founderType === 'CORPORATE'
              ? formData.aiAnalysis?.businessNumber
              : undefined,
          profileCompanies: profileResult?.data?.companyUsers,
        });

        if (formData.serviceType === 'STARTUP') {
          if (resolvedCompanyId == null) {
            toastUtils.error(
              '회사 등록은 완료되었으나 회사 식별 정보를 받지 못했습니다. 다시 시도해주세요.',
            );
            return;
          }
          await spaceService.applySpace(resolvedCompanyId, 'STARTUP');
          toastUtils.success('회사 등록이 완료되었습니다.');
          navigate('/space-management');
        } else if (formData.serviceType === 'INVESTOR') {
          if (resolvedCompanyId == null) {
            toastUtils.error(
              '회사 등록은 완료되었으나 회사 식별 정보를 받지 못했습니다. 관리자에게 문의해주세요.',
            );
            return;
          }
          setRegisteredCompanyId(resolvedCompanyId);
          setCurrentStep('PLAN_SELECT');
        }
      } else {
        toastUtils.error(result.message || '회사 등록 중 오류가 발생했습니다.');
      }      
      
    } catch (error) {
      toastUtils.error('회사 등록 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 뒤로가기 핸들러
   */
  const handleBack = () => {
    switch (currentStep) {
      case 'SERVICE_SELECT':        
        break;
      case 'FOUNDER_TYPE':
        setCurrentStep('SERVICE_SELECT');
        break;
      case 'FORM_INPUT':
      case 'BUSINESS_UPLOAD':
        setCurrentStep('FOUNDER_TYPE');
        break;
      case 'VERIFICATION':
        setCurrentStep('BUSINESS_UPLOAD');
        break;
      case 'PLAN_SELECT':
        // 이미 회사가 등록된 상태이므로 뒤로갈 수 없음
        break;
    }
  };

  /**
   * 단계별 렌더링 함수
   */
  const renderStepContent = () => {
    switch (currentStep) {
      case 'SERVICE_SELECT':
        return renderServiceSelect();
      case 'FOUNDER_TYPE':
        return renderFounderTypeSelect();
      case 'FORM_INPUT':
        return renderFormInput();
      case 'BUSINESS_UPLOAD':
        return renderBusinessUpload();
      case 'VERIFICATION':
        return renderVerification();
      case 'PLAN_SELECT':
        return renderPlanSelect();
      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 'SERVICE_SELECT':
        return '사용할 서비스를 선택해주세요';
      case 'FOUNDER_TYPE':
        return '창업자 유형을 선택해주세요';
      case 'FORM_INPUT':
        return formData.founderType === 'PRELIMINARY' ? '예비 창업자 정보' : '회사 정보';
      case 'BUSINESS_UPLOAD':
        return verificationBusinessInfo === 'READY' ? '사업자등록증을 업로드해주세요' : '사업자 정보를 직접 입력해주세요';
      case 'VERIFICATION':
        return '사업자 정보를 직접 입력해주세요';
      case 'PLAN_SELECT':
        return '이용할 플랜을 선택해주세요';
    }
  }

  /**
   * 1단계: 서비스 선택
   */
  const renderServiceSelect = () => (
    <Section>
      <SectionDescription>
        비즈니스에 맞는 서비스를 선택하시면 해당 서비스에 특화된 기능을 제공합니다
      </SectionDescription>
      
      <ServiceGrid>
        {serviceTypes.map((service) => (
          <ServiceCard
            key={service.type}
            $disabled={service.isPreparing}
            onClick={() => {
              if (service.isPreparing) {
                toastUtils.info('서비스 준비 중입니다.');
                return;
              }
              handleServiceSelect(service.type);
            }}
          >
            {service.isPreparing && <PreparingBadge>준비중</PreparingBadge>}
            <ServiceHeader>
              <ServiceIcon>{service.icon}</ServiceIcon>
              <ServiceInfo>
                <ServiceName>{service.name}</ServiceName>
                <ServiceDescription>{service.description}</ServiceDescription>
              </ServiceInfo>
            </ServiceHeader>
            
            <FeatureList>
              {service.features.map((feature, index) => (
                <FeatureItem key={index}>
                  <FeatureIcon>•</FeatureIcon>
                  <FeatureText>{feature}</FeatureText>
                </FeatureItem>
              ))}
            </FeatureList>
          </ServiceCard>
        ))}
      </ServiceGrid>
    </Section>
  );

  /**
   * 2단계: 창업자 타입 선택
   */
  const renderFounderTypeSelect = () => (
    <Section>
      <SectionDescription>
        현재 상황에 맞는 유형을 선택하시면 적절한 등록 절차를 안내해드립니다
      </SectionDescription>
      
      <FounderTypeGrid>
        <FounderTypeCard
          onClick={() => {
            if (formData.serviceType === 'STARTUP') {
              handleFounderTypeSelect('PRELIMINARY');
            }
          }}
          disabled={formData.serviceType !== 'STARTUP'}
        >
          <FounderTypeIconWrapper $disabled={formData.serviceType !== 'STARTUP'}>
            <User size={32} />
          </FounderTypeIconWrapper>
          <FounderTypeInfo>
            <FounderTypeName disabled={formData.serviceType !== 'STARTUP'}>
              예비 창업자
            </FounderTypeName>
            <FounderTypeDesc disabled={formData.serviceType !== 'STARTUP'}>
              사업자등록증이 없는 예비 창업자
              {formData.serviceType !== 'STARTUP' && (
                <DisabledText>(스타트업 서비스만 가능)</DisabledText>
              )}
            </FounderTypeDesc>
          </FounderTypeInfo>
        </FounderTypeCard>
        
        <FounderTypeCard onClick={() => handleFounderTypeSelect('CORPORATE')}>
          <FounderTypeIconWrapper>
            <Building2 size={32} />
          </FounderTypeIconWrapper>
          <FounderTypeInfo>
            <FounderTypeName>법인 창업자</FounderTypeName>
            <FounderTypeDesc>사업자등록증이 있는 법인 사업자</FounderTypeDesc>
          </FounderTypeInfo>
        </FounderTypeCard>
      </FounderTypeGrid>
    </Section>
  );

  /**
   * 3단계: 정보 입력 폼
   */
  const renderFormInput = () => (
    <Section>
      {/* 법인 창업자인 경우 검증된 회사 정보 표시 */}
      {formData.founderType === 'CORPORATE' && formData.aiAnalysis && (
        <VerifiedCompanyCard>
          <VerifiedCompanyHeader>
            <VerifiedBadge>
              <Check size={14} />
              <span>인증됨</span>
            </VerifiedBadge>
            <VerifiedCompanyName>{formData.aiAnalysis.companyName}</VerifiedCompanyName>
          </VerifiedCompanyHeader>
          <VerifiedCompanyInfo>
            <VerifiedInfoItem>
              <VerifiedInfoLabel>사업자등록번호</VerifiedInfoLabel>
              <VerifiedInfoValue>{formData.aiAnalysis.businessNumber}</VerifiedInfoValue>
            </VerifiedInfoItem>
            <VerifiedInfoItem>
              <VerifiedInfoLabel>대표자</VerifiedInfoLabel>
              <VerifiedInfoValue>{formData.aiAnalysis.representative}</VerifiedInfoValue>
            </VerifiedInfoItem>
            <VerifiedInfoItem>
              <VerifiedInfoLabel>법인등록번호</VerifiedInfoLabel>
              <VerifiedInfoValue>{formData.aiAnalysis.corporateNumber || '-'}</VerifiedInfoValue>
            </VerifiedInfoItem>
            <VerifiedInfoItem>
              <VerifiedInfoLabel>설립일</VerifiedInfoLabel>
              <VerifiedInfoValue>{formData.aiAnalysis.establishedDate}</VerifiedInfoValue>
            </VerifiedInfoItem>
          </VerifiedCompanyInfo>
        </VerifiedCompanyCard>
      )}

      <FormSection>
        <FormSectionTitle>
          {formData.founderType === 'PRELIMINARY' ? '예비 창업자 정보' : '회사 추가 정보'}
        </FormSectionTitle>
        <FormSectionDesc>
          {formData.founderType === 'PRELIMINARY' 
            ? '예비 창업자 정보를 입력해주세요'
            : '팩트시트에서 사용할 회사 정보를 입력해주세요'
          }
        </FormSectionDesc>
        
        <FormGrid>
          {formData.founderType === 'PRELIMINARY' && (
            <FormGroup $fullWidth>
              <Label>회사명 *</Label>
              <Input
                name="companyName"
                placeholder="회사명을 입력해주세요"
                value={formData.companyName || ''}
                onChange={handleInputChange}
              />
            </FormGroup>
          )}
          
          <FormGroup>
            <Label>회사 이메일 *</Label>
            <CodeInputGroup>
              <Input
                name="companyEmail"
                placeholder="company@example.com"
                value={formData.companyEmail}
                onChange={handleInputChange}
              />
              <CheckCodeButton
                type="button"
                onClick={handleCheckCompanyEmail}
                disabled={isCheckingEmail || !formData.companyEmail}
                $isChecked={isEmailChecked}
              >
                {isCheckingEmail ? '확인 중...' : isEmailChecked ? '확인완료' : '중복확인'}
              </CheckCodeButton>
            </CodeInputGroup>
            <HelpText>가능하면 회사 도메인 이메일을 사용해주세요</HelpText>
            {errors.companyEmail && <ErrorMessage>{errors.companyEmail}</ErrorMessage>}
          </FormGroup>
          
          <FormGroup>
            <Label>회사 코드 *</Label>
            <CodeInputGroup>
              <Input
                name="companyCode"
                placeholder="회사코드"
                value={formData.companyCode}
                onChange={handleInputChange}
              />
              <CheckCodeButton
                type="button"
                onClick={handleCheckCompanyCode}
                disabled={isCheckingCode || !formData.companyCode}
                $isChecked={isCodeChecked}
              >
                {isCheckingCode ? '확인 중...' : isCodeChecked ? '확인완료' : '중복확인'}
              </CheckCodeButton>
            </CodeInputGroup>
            <HelpText>영문과 숫자 조합, 4자 이상 (예: factsheet01)</HelpText>
            {errors.companyCode && <ErrorMessage>{errors.companyCode}</ErrorMessage>}
          </FormGroup>
        </FormGrid>
      </FormSection>

      {/* 예비 창업자만 대표자 정보 섹션 표시 */}
      {formData.founderType === 'PRELIMINARY' && (
        <FormSection>
          <FormSectionTitle>대표자 정보</FormSectionTitle>
          
          <FormGrid>
            <FormGroup $fullWidth>
              <Label>본인이 대표자입니까?</Label>
              <RadioCardGroup>
                <RadioCard 
                  $isSelected={formData.isRepresentative === true}
                  onClick={() => setFormData(prev => ({ ...prev, isRepresentative: true }))}
                >
                  <RadioCardIcon $isSelected={formData.isRepresentative === true}>
                    {formData.isRepresentative === true ? <Check size={16} /> : null}
                  </RadioCardIcon>
                  <RadioCardText>예, 본인이 대표자입니다</RadioCardText>
                </RadioCard>
                <RadioCard 
                  $isSelected={formData.isRepresentative === false}
                  onClick={() => setFormData(prev => ({ ...prev, isRepresentative: false }))}
                >
                  <RadioCardIcon $isSelected={formData.isRepresentative === false}>
                    {formData.isRepresentative === false ? <Check size={16} /> : null}
                  </RadioCardIcon>
                  <RadioCardText>아니오, 다른 분이 대표자입니다</RadioCardText>
                </RadioCard>
              </RadioCardGroup>
            </FormGroup>
            
            {formData.isRepresentative === false && (
              <>
                <FormGroup>
                  <Label>대표자 이름 *</Label>
                  <Input
                    name="ceoName"
                    placeholder="대표자 이름을 입력해주세요"
                    value={formData.ceoName || ''}
                    onChange={handleInputChange}
                  />
                  {errors.ceoName && <ErrorMessage>{errors.ceoName}</ErrorMessage>}
                </FormGroup>
                
                <FormGroup>
                  <Label>대표자 이메일 *</Label>
                  <Input
                    name="ceoEmail"
                    placeholder="representative@example.com"
                    value={formData.ceoEmail || ''}
                    onChange={handleInputChange}
                  />
                  {errors.ceoEmail && <ErrorMessage>{errors.ceoEmail}</ErrorMessage>}
                </FormGroup>
              </>
            )}
          </FormGrid>
        </FormSection>
      )}
      
      <ActionButtons>
        <Button type="button" $variant="outline" onClick={handleBack}>
          이전
        </Button>
        <Button 
          type="button" 
          $variant="fill" 
          onClick={handleFinalSubmit}
          disabled={isLoading || !isCodeChecked || !isEmailChecked || !formData.companyEmail || !formData.companyCode}
        >
          {isLoading ? '등록 중...' : '회사 등록 완료'}
        </Button>
      </ActionButtons>
    </Section>
  );

  /**
   * 4단계: 사업자등록증 업로드
   */
  const renderBusinessUpload = () => (
    <>
    {verificationBusinessInfo === "READY" ? (
      <Section>
        <SectionDescription>
          최신 사업자등록증을 업로드하고 인증 버튼을 눌러주세요. AI가 자동으로 회사 정보를 분석합니다.
        </SectionDescription>
        
        <FileUpload
          type="md"
          accept=".pdf"
          maxSize={10 * 1024 * 1024}
          files={uploadedFile ? [uploadedFile] : []}
          onChange={handleFileUpload}
          onError={(error) => toastUtils.error(error)}
          label="사업자등록증을 선택하거나 드래그하세요"
          disabled={isVerifying}
        />
        {isVerifying && (
          <LoadingMessage>
            <LoadingIconWrapper>
              <Loader2 size={20} />
            </LoadingIconWrapper>
            AI가 사업자등록증을 분석하고 있습니다...
          </LoadingMessage>
        )}
        
        <ActionButtons>
          <Button type="button" $variant="outline" onClick={handleBack}>
            이전
          </Button>
          <Button
            type="button"
            $variant="fill"
            disabled={!uploadedFile || isVerifying}
            onClick={handleVerification}
          >
            {isVerifying ? '인증 중...' : '인증하기'}
          </Button>
        </ActionButtons>      
      </Section>
    ) : verificationBusinessInfo === "EXIST_COMPANY_EXIST_MASTER" ? (
        <Section>
          <ExistingCompanyContainer>
            <CompanyFoundIconWrapper>
              <Building2 size={36} />
            </CompanyFoundIconWrapper>
            <CompanyFoundTitle>해당 회사는 이미 팩트시트에 등록되어 있습니다</CompanyFoundTitle>
            <CompanyFoundDescription>
              회사 정보를 확인하고 조직원으로 가입 신청하시겠습니까?
            </CompanyFoundDescription>
          </ExistingCompanyContainer>
          
          <ExistingCompanyInfoCard>
            <CompanyInfoHeader>
              <CompanyInfoIconWrapper>
                <Building2 size={20} />
              </CompanyInfoIconWrapper>
              <CompanyInfoTitle>회사 정보</CompanyInfoTitle>
              <CompanyStatusBadge $status="active">운영 중</CompanyStatusBadge>
            </CompanyInfoHeader>
            
            <CompanyInfoGrid>
              <CompanyInfoItem>
                <CompanyInfoLabel>회사명</CompanyInfoLabel>
                <CompanyInfoValue>{existingCompanyInfo!.name}</CompanyInfoValue>
              </CompanyInfoItem>
              
              <CompanyInfoItem>
                <CompanyInfoLabel>사업자등록번호</CompanyInfoLabel>
                <CompanyInfoValue>{existingCompanyInfo!.businessNumber}</CompanyInfoValue>
              </CompanyInfoItem>
              
              <CompanyInfoItem>
                <CompanyInfoLabel>대표자</CompanyInfoLabel>
                <CompanyInfoValue>{existingCompanyInfo!.ceoName}</CompanyInfoValue>
              </CompanyInfoItem>
              
              <CompanyInfoItem>
                <CompanyInfoLabel>마스터</CompanyInfoLabel>
                <CompanyInfoValue>
                  <MasterUserInfo>                    
                    <MasterUserDetails>
                      <MasterUserName>{existingCompanyInfo!.masterUser!.name}</MasterUserName>
                      <MasterUserEmail>{existingCompanyInfo!.masterUser!.email}</MasterUserEmail>
                    </MasterUserDetails>
                  </MasterUserInfo>
                </CompanyInfoValue>
              </CompanyInfoItem>
            </CompanyInfoGrid>
          </ExistingCompanyInfoCard>
          
          <JoinRequestNotice>
            <NoticeIconWrapper $type="info">
              <Briefcase size={18} />
            </NoticeIconWrapper>
            <NoticeText>
              <strong>조직원 가입 신청</strong><br />
              가입 신청 후 회사 마스터의 승인을 받으시면 해당 회사의 조직원으로 등록됩니다.
            </NoticeText>
          </JoinRequestNotice>
          
          <JoinRequestEmailSection>
            <EmailSectionTitle>가입 신청에 사용할 이메일</EmailSectionTitle>
            <CodeInputGroup>
              <Input
                type="text"
                placeholder="example@email.com"
                value={joinRequestEmail}
                onChange={(e) => {
                  setJoinRequestEmail(e.target.value);
                  setIsJoinEmailChecked(false);
                }}
              />
              <CheckCodeButton
                type="button"                
                onClick={handleCheckJoinEmail}
                disabled={isCheckingJoinEmail || !joinRequestEmail}
                $isChecked={isJoinEmailChecked}
              >
                {isCheckingJoinEmail ? '확인중...' : isJoinEmailChecked ? '확인완료' : '중복확인'}
              </CheckCodeButton>
            </CodeInputGroup>
            <EmailHelpText>회사에서 사용할 이메일 주소를 입력해주세요.</EmailHelpText>
          </JoinRequestEmailSection>
          
          <ActionButtons>
            <Button type="button" $variant="outline" onClick={() => {
              setVerificationBusinessInfo("READY");
              setUploadedFile(null);
              setFormData(prev => ({
                ...prev,
                businessFile: undefined,
                businessFileId: undefined,
                aiAnalysis: undefined,
              }));
              setExistingCompanyInfo(undefined);
              setJoinRequestEmail('');
              setIsJoinEmailChecked(false);
              setErrors({});
            }}>
              다른 회사 찾기
            </Button>
            <Button 
              type="button" 
              $variant="fill" 
              onClick={handleEmployeeJoinRequest}
              disabled={isLoading || !joinRequestEmail.trim() || !isJoinEmailChecked}
            >
              {isLoading ? '신청 중...' : '직원 가입 신청'}
            </Button>
          </ActionButtons>
        </Section>
    ) : verificationBusinessInfo === "EXIST_COMPANY_NOT_EXIST_MASTER" ? (
        <Section>
          <ExistingCompanyContainer>
            <CompanyFoundIconWrapper>
              <Building2 size={36} />
            </CompanyFoundIconWrapper>
            <CompanyFoundTitle>등록된 회사를 찾았습니다</CompanyFoundTitle>
            <CompanyFoundDescription>
              회사는 등록되어 있지만 마스터가 없습니다. 이 회사의 마스터로 등록하시겠습니까?
            </CompanyFoundDescription>
          </ExistingCompanyContainer>
          
          <ExistingCompanyInfoCard>
            <CompanyInfoHeader>
              <CompanyInfoIconWrapper>
                <Building2 size={20} />
              </CompanyInfoIconWrapper>
              <CompanyInfoTitle>회사 정보</CompanyInfoTitle>
              <CompanyStatusBadge $status="pending">마스터 없음</CompanyStatusBadge>
            </CompanyInfoHeader>
            
            <CompanyInfoGrid>
              <CompanyInfoItem>
                <CompanyInfoLabel>회사명</CompanyInfoLabel>
                <CompanyInfoValue>{existingCompanyInfo!.name}</CompanyInfoValue>
              </CompanyInfoItem>
              
              <CompanyInfoItem>
                <CompanyInfoLabel>사업자등록번호</CompanyInfoLabel>
                <CompanyInfoValue>{existingCompanyInfo!.businessNumber}</CompanyInfoValue>
              </CompanyInfoItem>
              
              <CompanyInfoItem>
                <CompanyInfoLabel>대표자</CompanyInfoLabel>
                <CompanyInfoValue>{existingCompanyInfo!.ceoName}</CompanyInfoValue>
              </CompanyInfoItem>
                          
              <CompanyInfoItem>
                <CompanyInfoLabel>마스터</CompanyInfoLabel>
                <CompanyInfoValue>
                  <NoMasterInfo>
                    <NoMasterIconWrapper>
                      <UserX size={16} />
                    </NoMasterIconWrapper>
                    <NoMasterText>등록된 마스터가 없습니다</NoMasterText>
                  </NoMasterInfo>
                </CompanyInfoValue>
              </CompanyInfoItem>
            </CompanyInfoGrid>
          </ExistingCompanyInfoCard>
          
          <MasterRegistrationNotice>
            <NoticeIconWrapper $type="warning">
              <Crown size={18} />
            </NoticeIconWrapper>
            <NoticeText>
              <strong>마스터 등록</strong><br />
              이 회사의 마스터로 등록되면 회사 정보 관리, 직원 승인, 서비스 설정 등의 권한을 갖게 됩니다.
            </NoticeText>
          </MasterRegistrationNotice>
          
          <MasterRegistrationFormSection>
            <MasterFormTitle>회사 정보 입력</MasterFormTitle>
            <MasterFormGrid>
              <MasterFormGroup>
                <Label>회사에서 사용할 이메일 *</Label>
                <CodeInputGroup>
                  <Input
                    type="text"
                    placeholder="company@example.com"
                    value={masterCompanyEmail}
                    onChange={(e) => {
                      setMasterCompanyEmail(e.target.value);
                      setIsMasterEmailChecked(false);
                    }}
                  />
                  <CheckCodeButton
                    type="button"
                    onClick={handleCheckMasterCompanyEmail}
                    disabled={isCheckingMasterEmail || !masterCompanyEmail}
                    $isChecked={isMasterEmailChecked}
                  >
                    {isCheckingMasterEmail ? '확인중...' : isMasterEmailChecked ? '확인완료' : '중복확인'}
                  </CheckCodeButton>
                </CodeInputGroup>
                <HelpText>회사에서 사용할 이메일 주소를 입력해주세요</HelpText>
              </MasterFormGroup>
              
              <MasterFormGroup>
                <Label>회사 코드 *</Label>
                <CodeInputGroup>
                  <Input
                    type="text"
                    placeholder="회사코드"
                    value={masterCompanyCode}
                    onChange={(e) => {
                      const formatted = formatCompanyCode(e.target.value);
                      setMasterCompanyCode(formatted);
                      setIsMasterCodeChecked(false);
                    }}
                  />
                  <CheckCodeButton
                    type="button"
                    onClick={handleCheckMasterCompanyCode}
                    disabled={isCheckingMasterCode || !masterCompanyCode}
                    $isChecked={isMasterCodeChecked}
                  >
                    {isCheckingMasterCode ? '확인중...' : isMasterCodeChecked ? '확인완료' : '중복확인'}
                  </CheckCodeButton>
                </CodeInputGroup>
                <HelpText>영문과 숫자 조합, 4자 이상 (예: factsheet01)</HelpText>
              </MasterFormGroup>
              
            </MasterFormGrid>
          </MasterRegistrationFormSection>
          
          <ActionButtons>
            <Button type="button" $variant="outline" onClick={() => {
              setVerificationBusinessInfo("READY");
              setUploadedFile(null);
              setFormData(prev => ({
                ...prev,
                businessFile: undefined,
                businessFileId: undefined,
                aiAnalysis: undefined,
              }));
              setExistingCompanyInfo(undefined);
              setJoinRequestEmail('');
              setMasterCompanyEmail('');
              setMasterCompanyCode('');
              setIsMasterEmailChecked(false);
              setIsMasterCodeChecked(false);
              setIsJoinEmailChecked(false);
              setErrors({});
            }}>
              다른 회사 찾기
            </Button>
            <Button 
              type="button" 
              $variant="fill" 
              onClick={handleMasterRegistration}
              disabled={isLoading || !isMasterEmailChecked || !isMasterCodeChecked || !masterCompanyEmail.trim() || !masterCompanyCode.trim()}
            >
              {isLoading ? '등록 중...' : '마스터로 등록'}
            </Button>
          </ActionButtons>
        </Section>
    ) : (
      <Section>        
        <SectionDescription style={{ color: '#f59e0b' }}>
          {verificationBusinessInfo === "EMPTY_DATA" && "AI 분석이 완료되지 않았거나 일부 정보가 누락되었습니다."}
          {verificationBusinessInfo === "FAILED" && "사업자 정보 인증이 실패했습니다."}
          정확한 정보를 직접 입력해주세요.
        </SectionDescription>
        
        <BusinessInfoContainer>
          <BusinessInfoTitle>
            <EditIconWrapper>
              <Pencil size={14} />
            </EditIconWrapper>
            사업자 정보 직접 입력
          </BusinessInfoTitle>
          <BusinessInfoGrid>
            <FormGroup>
              <Label>사업자명 *</Label>
              <Input
                placeholder="사업자명을 입력하세요"
                value={formData.aiAnalysis?.companyName || ''}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    aiAnalysis: {
                      ...prev.aiAnalysis,
                      companyName: e.target.value,
                      businessNumber: prev.aiAnalysis?.businessNumber || '',
                      representative: prev.aiAnalysis?.representative || '',
                      corporateNumber: prev.aiAnalysis?.corporateNumber || '',
                      establishedDate: prev.aiAnalysis?.establishedDate || '',
                    }
                  }));
                  // 에러 초기화
                  if (errors.companyName) {
                    setErrors(prev => ({ ...prev, companyName: undefined }));
                  }
                }}
              />
              {errors.companyName && <ErrorMessage>{errors.companyName}</ErrorMessage>}
            </FormGroup>
            
            <FormGroup>
              <Label>사업자등록번호 *</Label>
              <Input
                placeholder="000-00-00000"
                maxLength={12}
                value={formData.aiAnalysis?.businessNumber || ''}
                onChange={(e) => {
                  const formatted = formatBusinessNumber(e.target.value);
                  setFormData(prev => ({
                    ...prev,
                    aiAnalysis: {
                      ...prev.aiAnalysis,
                      companyName: prev.aiAnalysis?.companyName || '',
                      businessNumber: formatted,
                      representative: prev.aiAnalysis?.representative || '',
                      corporateNumber: prev.aiAnalysis?.corporateNumber || '',
                      establishedDate: prev.aiAnalysis?.establishedDate || '',
                    }
                  }));
                  // 에러 초기화
                  if (errors.businessNumber) {
                    setErrors(prev => ({ ...prev, businessNumber: undefined }));
                  }
                }}
              />
              {errors.businessNumber && <ErrorMessage>{errors.businessNumber}</ErrorMessage>}
            </FormGroup>
            
            <FormGroup>
              <Label>대표자 *</Label>
              <Input
                placeholder="대표자명을 입력하세요"
                value={formData.aiAnalysis?.representative || ''}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    aiAnalysis: {
                      ...prev.aiAnalysis,
                      companyName: prev.aiAnalysis?.companyName || '',
                      businessNumber: prev.aiAnalysis?.businessNumber || '',
                      representative: e.target.value,
                      corporateNumber: prev.aiAnalysis?.corporateNumber || '',
                      establishedDate: prev.aiAnalysis?.establishedDate || '',
                    }
                  }));
                  // 에러 초기화
                  if (errors.representative) {
                    setErrors(prev => ({ ...prev, representative: undefined }));
                  }
                }}
              />
              {errors.representative && <ErrorMessage>{errors.representative}</ErrorMessage>}
            </FormGroup>
            
            <FormGroup>
              <Label>법인등록번호 *</Label>
              <Input
                placeholder="123456-1234567"
                maxLength={14}
                value={formData.aiAnalysis?.corporateNumber || ''}
                onChange={(e) => {
                  const formatted = formatCorporateNumber(e.target.value);
                  setFormData(prev => ({
                    ...prev,
                    aiAnalysis: {
                      ...prev.aiAnalysis,
                      companyName: prev.aiAnalysis?.companyName || '',
                      businessNumber: prev.aiAnalysis?.businessNumber || '',
                      representative: prev.aiAnalysis?.representative || '',
                      corporateNumber: formatted,
                      establishedDate: prev.aiAnalysis?.establishedDate || '',
                    }
                  }));
                  // 에러 초기화
                  if (errors.corporateNumber) {
                    setErrors(prev => ({ ...prev, corporateNumber: undefined }));
                  }
                }}
              />
              {errors.corporateNumber && <ErrorMessage>{errors.corporateNumber}</ErrorMessage>}
            </FormGroup>
            
            <FormGroup>
              <Label>설립일 *</Label>
              <Input
                placeholder="YYYY-MM-DD"
                maxLength={10}
                value={formData.aiAnalysis?.establishedDate || ''}
                onChange={(e) => {
                  const formatted = formatEstablishmentDate(e.target.value);
                  setFormData(prev => ({
                    ...prev,
                    aiAnalysis: {
                      ...prev.aiAnalysis,
                      companyName: prev.aiAnalysis?.companyName || '',
                      businessNumber: prev.aiAnalysis?.businessNumber || '',
                      representative: prev.aiAnalysis?.representative || '',
                      corporateNumber: prev.aiAnalysis?.corporateNumber || '',
                      establishedDate: formatted,
                    }
                  }));
                  // 에러 초기화
                  if (errors.establishedDate) {
                    setErrors(prev => ({ ...prev, establishedDate: undefined }));
                  }
                }}
              />
              {errors.establishedDate && <ErrorMessage>{errors.establishedDate}</ErrorMessage>}
            </FormGroup>
          </BusinessInfoGrid>
          
          <ActionButtons>
            <Button type="button" $variant="outline" onClick={() => {
              setVerificationBusinessInfo("READY");
              setUploadedFile(null);
              setFormData(prev => ({
                ...prev,
                businessFile: undefined,
                businessFileId: undefined,
                aiAnalysis: undefined,
              }));
              setErrors({});
            }}>
              다시 업로드
            </Button>
            <Button 
              type="button" 
              $variant="fill" 
              onClick={handleManualVerification}
              disabled={isVerifying || !formData.aiAnalysis?.companyName || !formData.aiAnalysis?.businessNumber || 
                       !formData.aiAnalysis?.representative || 
                       !formData.aiAnalysis?.establishedDate}
            >
              {isVerifying ? '인증 중...' : '정보 확인하기'}
            </Button>
          </ActionButtons>
          
          {isVerifying && (
            <LoadingMessage>
              <LoadingIconWrapper>
                <Loader2 size={20} />
              </LoadingIconWrapper>
              사업자 정보를 확인하고 있습니다...
            </LoadingMessage>
          )}
        </BusinessInfoContainer>
      </Section>
    )}
    </>
  );

  /**
   * 5단계: AI 분석 결과 확인
   */
  const renderVerification = () => {
    // AI 분석 성공 여부 확인
    const hasAIAnalysis = formData.aiAnalysis;
    
    if (hasAIAnalysis) {
      // AI 분석 성공 - 결과 표시
      return (
        <Section>
          <VerificationSuccessContainer>
            <SuccessIconWrapper>
              <CheckCircle2 size={28} />
            </SuccessIconWrapper>
            <SuccessMessage>
              사업자 정보 인증이 완료되었습니다. 분석된 정보를 확인해주세요.
            </SuccessMessage>
          </VerificationSuccessContainer>
          
          <BusinessInfoContainer>
            <BusinessInfoTitle>
              <CheckIconWrapper>
                <Check size={14} />
              </CheckIconWrapper>
              인증된 사업자 정보
            </BusinessInfoTitle>
            <BusinessInfoGrid>
              <FormGroup>
                <Label>사업자명</Label>
                <Input value={formData.aiAnalysis?.companyName || ''} readOnly />
              </FormGroup>
              
              <FormGroup>
                <Label>사업자등록번호</Label>
                <Input value={formData.aiAnalysis?.businessNumber || ''} readOnly />
              </FormGroup>
              
              <FormGroup>
                <Label>대표자</Label>
                <Input value={formData.aiAnalysis?.representative || ''} readOnly />
              </FormGroup>
              
              <FormGroup>
                <Label>법인등록번호</Label>
                <Input value={formData.aiAnalysis?.corporateNumber || ''} readOnly />
              </FormGroup>
              
              <FormGroup>
                <Label>설립일</Label>
                <Input value={formData.aiAnalysis?.establishedDate || ''} readOnly />
              </FormGroup>              
            </BusinessInfoGrid>
          </BusinessInfoContainer>
          
          <ActionButtons>
            <Button type="button" $variant="outline" onClick={handleBack}>
              다시 업로드
            </Button>
            <Button 
              type="button" 
              $variant="fill" 
              onClick={handleVerifyAndCheck}
              disabled={isLoading}
            >
              {isLoading ? '확인 중...' : '정보 확인 및 다음'}
            </Button>
          </ActionButtons>
        </Section>
      );
    } else {
      // AI 분석 실패 - 수동 입력
      return (
        <Section>
          <SectionTitle>사업자 정보를 직접 입력해주세요</SectionTitle>
          <SectionDescription style={{ color: '#f59e0b' }}>
            AI 분석이 완료되지 않았거나 일부 정보가 누락되었습니다. 정확한 정보를 직접 입력해주세요.
          </SectionDescription>
          
          <BusinessInfoContainer>
            <BusinessInfoTitle>
              <EditIconWrapper>
                <Pencil size={14} />
              </EditIconWrapper>
              사업자 정보 직접 입력
            </BusinessInfoTitle>
            <BusinessInfoGrid>
              <FormGroup>
                <Label>사업자명 *</Label>
                <Input
                  placeholder="사업자명을 입력하세요"
                  value={formData.aiAnalysis?.companyName || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    aiAnalysis: {
                      ...prev.aiAnalysis,
                      companyName: e.target.value,
                      businessNumber: prev.aiAnalysis?.businessNumber || '',
                      representative: prev.aiAnalysis?.representative || '',
                      corporateNumber: prev.aiAnalysis?.corporateNumber || '',
                      establishedDate: prev.aiAnalysis?.establishedDate || '',
                    }
                  }))}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>사업자등록번호 *</Label>
                <Input
                  placeholder="000-00-00000"
                  maxLength={12}
                  value={formData.aiAnalysis?.businessNumber || ''}
                  onChange={(e) => {
                    const formatted = formatBusinessNumber(e.target.value);
                    setFormData(prev => ({
                      ...prev,
                      aiAnalysis: {
                        ...prev.aiAnalysis,
                        companyName: prev.aiAnalysis?.companyName || '',
                        businessNumber: formatted,
                        representative: prev.aiAnalysis?.representative || '',
                        corporateNumber: prev.aiAnalysis?.corporateNumber || '',
                        establishedDate: prev.aiAnalysis?.establishedDate || '',
                      }
                    }));
                  }}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>대표자 *</Label>
                <Input
                  placeholder="대표자명을 입력하세요"
                  value={formData.aiAnalysis?.representative || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    aiAnalysis: {
                      ...prev.aiAnalysis,
                      companyName: prev.aiAnalysis?.companyName || '',
                      businessNumber: prev.aiAnalysis?.businessNumber || '',
                      representative: e.target.value,
                      corporateNumber: prev.aiAnalysis?.corporateNumber || '',
                      establishedDate: prev.aiAnalysis?.establishedDate || '',
                    }
                  }))}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>법인등록번호 *</Label>
                <Input
                  placeholder="123456-1234567"
                  maxLength={14}
                  value={formData.aiAnalysis?.corporateNumber || ''}
                  onChange={(e) => {
                    const formatted = formatCorporateNumber(e.target.value);
                    setFormData(prev => ({
                      ...prev,
                      aiAnalysis: {
                        ...prev.aiAnalysis,
                        companyName: prev.aiAnalysis?.companyName || '',
                        businessNumber: prev.aiAnalysis?.businessNumber || '',
                        representative: prev.aiAnalysis?.representative || '',
                        corporateNumber: formatted,
                        establishedDate: prev.aiAnalysis?.establishedDate || '',
                      }
                    }));
                  }}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>설립일 *</Label>
                <Input
                  placeholder="YYYY-MM-DD"
                  maxLength={10}
                  value={formData.aiAnalysis?.establishedDate || ''}
                  onChange={(e) => {
                    const formatted = formatEstablishmentDate(e.target.value);
                    setFormData(prev => ({
                      ...prev,
                      aiAnalysis: {
                        ...prev.aiAnalysis,
                        companyName: prev.aiAnalysis?.companyName || '',
                        businessNumber: prev.aiAnalysis?.businessNumber || '',
                        representative: prev.aiAnalysis?.representative || '',
                        corporateNumber: prev.aiAnalysis?.corporateNumber || '',
                        establishedDate: formatted,
                      }
                    }));
                  }}
                />
              </FormGroup>
            </BusinessInfoGrid>
            
            <ActionButtons>
              <Button type="button" $variant="outline" onClick={handleBack}>
                다시 업로드
              </Button>
              <Button 
                type="button" 
                $variant="fill" 
                onClick={handleVerifyAndCheck}
                disabled={isLoading || !formData.aiAnalysis?.companyName || !formData.aiAnalysis?.businessNumber || 
                         !formData.aiAnalysis?.representative || !formData.aiAnalysis?.corporateNumber || 
                         !formData.aiAnalysis?.establishedDate}
              >
                {isLoading ? '확인 중...' : '정보 확인하기'}
              </Button>
            </ActionButtons>
          </BusinessInfoContainer>
        </Section>
      );
    }
  };

  /**
   * 플랜 목록 로드 (PLAN_SELECT 진입 시, 회사 ID가 있을 때마다)
   * — `plans.length === 0` 조건은 deps에 plans가 없어 stale closure로 요청이 스킵될 수 있어 사용하지 않음.
   */
  useEffect(() => {
    if (currentStep !== 'PLAN_SELECT' || registeredCompanyId == null) {
      return;
    }

    let cancelled = false;

    const loadPlans = async () => {
      setIsLoadingPlans(true);
      try {
        const result = await billingService.getPlans('INVESTOR');
        if (cancelled) return;
        const list = Array.isArray(result) ? result : [];
        setPlans(list);
      } catch {
        if (!cancelled) {
          setPlans([]);
          toastUtils.error('플랜 정보를 불러오지 못했습니다.');
        }
      } finally {
        if (!cancelled) {
          setIsLoadingPlans(false);
        }
      }
    };

    void loadPlans();

    return () => {
      cancelled = true;
    };
  }, [currentStep, registeredCompanyId, planListRefreshToken]);

  /**
   * 플랜 선택 핸들러
   *
   * PlanSelectionModal의 create 플로우와 동일한 API 사용 규칙을 따른다.
   * - 별도 협의: 구독을 생성하지 않고 `createConsultationInquiry`만 접수
   * - 무료 플랜: `createFreeSubscription` 전용 엔드포인트 사용
   * - 유료 플랜: 카드 등록 여부 확인 후 `createSubscription`
   */
  /** 도입 문의 모달 제출 핸들러 */
  const handleSubmitConsultation = async () => {
    if (!consultationModal.productId || !registeredCompanyId) return;

    setIsSubmittingConsultation(true);
    try {
      const response = await billingService.createConsultationInquiry({
        companyId: registeredCompanyId,
        productId: consultationModal.productId,
        message: consultationMessage.trim() || undefined,
      });
      if (response.success) {
        setConsultationModal({ isOpen: false, productId: null, planName: '' });
        setConsultationMessage('');
        setPlanResult({
          type: 'CONSULTATION_SUBMITTED',
          planName: consultationModal.planName,
          message: response.message,
        });
      } else {
        toastUtils.error(response.message || '문의 접수에 실패했습니다.');
      }
    } catch {
      toastUtils.error('문의 접수 중 오류가 발생했습니다.');
    } finally {
      setIsSubmittingConsultation(false);
    }
  };

  const handlePlanSelect = async (plan: BillingProductItem) => {
    if (!registeredCompanyId) return;

    const { id: productId, price, isConsultationRequired, name: planName } = plan;
    const isFree = price === 0;

    // 1) 별도 협의: 메시지 입력 모달 열기
    if (isConsultationRequired) {
      setConsultationModal({ isOpen: true, productId, planName });
      setConsultationMessage('');
      return;
    }

    // 2) 무료 플랜: 전용 엔드포인트 (카드 등록 없이 바로 구독 생성)
    if (isFree) {
      setIsPlanProcessing(true);
      try {
        const response = await billingService.createFreeSubscription({
          companyId: registeredCompanyId,
          productId,
        });
        if (response.success) {
          setPlanResult({
            type: 'FREE_SUBSCRIBED',
            planName,
            message: response.message,
          });
        } else {
          toastUtils.error(response.message || '플랜 설정에 실패했습니다.');
        }
      } catch {
        toastUtils.error('플랜 설정 중 오류가 발생했습니다.');
      } finally {
        setIsPlanProcessing(false);
      }
      return;
    }

    // 3) 유료 플랜: 카드 등록 여부 확인
    setIsPlanProcessing(true);
    try {
      const status = await billingService.getBillingStatus(registeredCompanyId);
      if (status.hasBillingKey) {
        const response = await billingService.createSubscription({
          companyId: registeredCompanyId,
          productId,
        });
        if (response.success) {
          setPlanResult({
            type: 'PAID_SUBSCRIBED',
            planName,
            message: response.message,
            amount: response.amount,
            eventType: response.eventType,
          });
        } else {
          toastUtils.error(response.message || '플랜 설정에 실패했습니다.');
        }
        setIsPlanProcessing(false);
      } else {
        // 카드 미등록 → 팝업에서 등록 후 message 이벤트로 후속 처리.
        // 팝업이 열려 있는 동안에는 버튼이 중복 눌리지 않도록 isPlanProcessing을 유지.
        toastUtils.info('유료 플랜을 이용하려면 결제 수단 등록이 필요합니다.');
        setSelectedProductId(productId);
        openCardRegisterPopup(registeredCompanyId);
      }
    } catch {
      toastUtils.error('플랜 설정 중 오류가 발생했습니다.');
      setIsPlanProcessing(false);
    }
  };

  const openCardRegisterPopup = (companyId: number) => {
    const width = 800;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      `/billing/card-register?companyId=${companyId}`,
      'cardRegisterPopup',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
    );

    if (!popup) {
      toastUtils.error('팝업이 차단되었습니다. 팝업 차단을 해제해주세요.');
      setIsPlanProcessing(false);
      setSelectedProductId(null);
      return;
    }

    cardPopupRef.current = popup;

    // 사용자가 브라우저 X 버튼 또는 팝업 내부 취소로 닫은 경우에도
    // CARD_REGISTER_* 메시지가 오지 않을 수 있으므로, 닫힘을 주기적으로 감지해
    // 버튼 상태(isPlanProcessing)와 선택 상품(selectedProductId)을 복구한다.
    if (cardPopupWatcherRef.current) {
      clearInterval(cardPopupWatcherRef.current);
      cardPopupWatcherRef.current = null;
    }
    cardPopupWatcherRef.current = window.setInterval(() => {
      const current = cardPopupRef.current;

      // 메시지 이벤트로 이미 처리된 경우: ref가 null로 비어 있음 → watcher만 종료
      if (!current) {
        if (cardPopupWatcherRef.current) {
          clearInterval(cardPopupWatcherRef.current);
          cardPopupWatcherRef.current = null;
        }
        return;
      }

      // 팝업이 닫혔으면(취소/X 버튼/외부에서 닫힘 포함) 상태 복구
      if (current.closed) {
        cardPopupRef.current = null;
        if (cardPopupWatcherRef.current) {
          clearInterval(cardPopupWatcherRef.current);
          cardPopupWatcherRef.current = null;
        }
        setIsPlanProcessing(false);
        setSelectedProductId(null);
      }
    }, 300);
  };

  const handleCardRegisterMessage = useCallback(
    async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!event.data?.type?.startsWith('CARD_REGISTER_')) return;

      cardPopupRef.current = null;
      if (cardPopupWatcherRef.current) {
        clearInterval(cardPopupWatcherRef.current);
        cardPopupWatcherRef.current = null;
      }

      if (event.data.type === 'CARD_REGISTER_SUCCESS') {
        if (registeredCompanyId && selectedProductId) {
          setIsPlanProcessing(true);
          try {
            const response = await billingService.createSubscription({
              companyId: registeredCompanyId,
              productId: selectedProductId,
            });
            if (response.success) {
              const matchedPlan = plans.find((p) => p.id === selectedProductId);
              setPlanResult({
                type: 'PAID_SUBSCRIBED',
                planName: matchedPlan?.name ?? '선택한',
                message: response.message,
                amount: response.amount,
                eventType: response.eventType,
              });
            } else {
              toastUtils.error(response.message || '플랜 설정에 실패했습니다.');
            }
          } catch {
            toastUtils.error('플랜 설정 중 오류가 발생했습니다.');
          } finally {
            setIsPlanProcessing(false);
            setSelectedProductId(null);
          }
        }
      } else if (event.data.type === 'CARD_REGISTER_FAIL') {
        toastUtils.error(event.data.error || '카드 등록에 실패했습니다.');
        setIsPlanProcessing(false);
        setSelectedProductId(null);
      } else if (event.data.type === 'CARD_REGISTER_CANCEL') {
        toastUtils.info('카드 등록이 취소되었습니다.');
        setIsPlanProcessing(false);
        setSelectedProductId(null);
      }
    },
    [registeredCompanyId, selectedProductId, plans],
  );

  useEffect(() => {
    window.addEventListener('message', handleCardRegisterMessage);
    return () => window.removeEventListener('message', handleCardRegisterMessage);
  }, [handleCardRegisterMessage]);

  // 언마운트 시 팝업 감시 interval 정리
  useEffect(() => {
    return () => {
      if (cardPopupWatcherRef.current) {
        clearInterval(cardPopupWatcherRef.current);
        cardPopupWatcherRef.current = null;
      }
    };
  }, []);

  /**
   * 플랜 선택 결과 화면 (결제 완료/무료 구독 시작/문의 접수 완료)
   */
  const renderPlanResult = (result: PlanResult) => {
    const isRefund = result.type === 'PAID_SUBSCRIBED' && result.eventType === 'REFUND';
    const isConsultation = result.type === 'CONSULTATION_SUBMITTED';

    let icon: React.ReactNode;
    let title: string;
    let description: React.ReactNode = null;
    let amountBadge: React.ReactNode = null;

    if (isConsultation) {
      icon = <MessageSquare size={44} />;
      title = `${result.planName} 플랜 도입 문의가 접수되었습니다`;
      description = (
        <>
          담당자가 영업일 기준 1~2일 내로 연락드릴 예정입니다.
          <br />
          문의 접수 후에도 필요 시 다른 플랜을 선택하실 수 있습니다.
        </>
      );
    } else if (result.type === 'FREE_SUBSCRIBED') {
      icon = <CheckCircle2 size={44} />;
      title = '무료 구독이 시작되었습니다';
      description = (
        <>
          회사 등록이 완료되었습니다.
          <br />
          투자사 스페이스를 자유롭게 이용해보세요.
        </>
      );
    } else {
      icon = isRefund ? <RotateCcw size={44} /> : <CreditCard size={44} />;
      title = `${result.planName} 플랜 구독이 시작되었습니다`;

      if (result.amount != null && result.eventType === 'CHARGE') {
        amountBadge = (
          <PlanResultAmountBadge $variant="charge">
            결제 금액: {result.amount.toLocaleString()}원
          </PlanResultAmountBadge>
        );
      } else if (result.amount != null && result.eventType === 'REFUND') {
        amountBadge = (
          <PlanResultAmountBadge $variant="refund">
            환불 금액: {result.amount.toLocaleString()}원
          </PlanResultAmountBadge>
        );
      }
    }

    return (
      <Section>
        <PlanResultContainer>
          <PlanResultIconWrapper
            $variant={isRefund ? 'refund' : isConsultation ? 'info' : 'success'}
          >
            {icon}
          </PlanResultIconWrapper>
          <PlanResultTitle>{title}</PlanResultTitle>
          {!isConsultation && <PlanResultPlanName>{result.planName} 플랜</PlanResultPlanName>}
          {amountBadge}
          {description && <PlanResultDescription>{description}</PlanResultDescription>}
          {result.message && <PlanResultMessage>{result.message}</PlanResultMessage>}

          <PlanResultConfirmButton type="button" onClick={() => navigate('/space-management')}>
            회사 관리로 이동
            <ArrowRight size={18} />
          </PlanResultConfirmButton>
        </PlanResultContainer>
      </Section>
    );
  };

  /**
   * 4단계: 플랜 선택 (INVESTOR 전용)
   */
  const renderPlanSelect = () => {
    if (registeredCompanyId == null) {
      return (
        <Section>
          <PlanGuideBanner>
            <PlanGuideIconWrapper>
              <Sparkles size={20} />
            </PlanGuideIconWrapper>
            <PlanGuideContent>
              <PlanGuideTitle>회사 등록이 완료되었습니다!</PlanGuideTitle>
              <PlanGuideDescription>
                회사 식별 정보를 불러올 수 없어 플랜을 표시할 수 없습니다. 페이지를 새로고침하거나 관리자에게
                문의해주세요.
              </PlanGuideDescription>
            </PlanGuideContent>
          </PlanGuideBanner>
        </Section>
      );
    }

    // 결제/구독 처리가 완료된 경우 결과 화면 표시
    if (planResult) {
      return renderPlanResult(planResult);
    }

    return (
    <Section>
      <PlanGuideBanner>
        <PlanGuideIconWrapper>
          <Sparkles size={20} />
        </PlanGuideIconWrapper>
        <PlanGuideContent>
          <PlanGuideTitle>회사 등록이 완료되었습니다!</PlanGuideTitle>
          <PlanGuideDescription>
            투자사 스페이스에서 사용할 플랜을 선택해주세요. 무료 플랜으로 시작하고 언제든 업그레이드할 수 있습니다.
          </PlanGuideDescription>
        </PlanGuideContent>
      </PlanGuideBanner>

      {isLoadingPlans ? (
        <PlanLoadingWrapper>
          <Loader2 size={24} className="animate-spin" />
          <span>플랜 정보를 불러오는 중...</span>
        </PlanLoadingWrapper>
      ) : plans.length === 0 ? (
        <PlanEmptyWrapper>
          <span>등록된 플랜이 없거나 목록을 불러오지 못했습니다.</span>
          <Button type="button" $variant="outline" onClick={() => setPlanListRefreshToken((n) => n + 1)}>
            다시 불러오기
          </Button>
        </PlanEmptyWrapper>
      ) : (
        <PlanSelectGrid>
          {plans.map((plan) => {
            const isFree = plan.price === 0;
            const isConsultation = plan.isConsultationRequired;

            return (
              <PlanSelectCard key={plan.id} $isHighlight={isFree && !isConsultation}>
                {isFree && !isConsultation && <PlanFreeBadge>무료</PlanFreeBadge>}
                {isConsultation && <PlanConsultationBadge>별도 협의</PlanConsultationBadge>}

                <PlanSelectHeader>
                  <PlanSelectName>{plan.name}</PlanSelectName>
                  {plan.subName && <PlanSelectSubName>{plan.subName}</PlanSelectSubName>}
                  <PlanSelectDescription>{plan.description ?? ''}</PlanSelectDescription>

                  <PlanSelectPriceWrapper>
                    {isConsultation ? (
                      <PlanSelectPrice>별도 협의</PlanSelectPrice>
                    ) : isFree ? (
                      <PlanSelectPrice>무료</PlanSelectPrice>
                    ) : (
                      <PlanSelectPriceContainer>
                        <PlanSelectPriceNumber>
                          {plan.price.toLocaleString()}
                        </PlanSelectPriceNumber>
                        <PlanSelectPriceUnit>
                          {plan.priceUnit} / {plan.billingPeriod === 'YEAR' ? '년' : '월'}
                        </PlanSelectPriceUnit>
                      </PlanSelectPriceContainer>
                    )}
                  </PlanSelectPriceWrapper>
                </PlanSelectHeader>

                {(plan.detailDescription || plan.limitValue != null) && (
                  <PlanSelectFeatureList>
                    {plan.limitValue != null && (
                      <PlanSelectFeatureItem>
                        <PlanSelectFeatureIcon>
                          <Check size={14} strokeWidth={3} />
                        </PlanSelectFeatureIcon>
                        <span>포트폴리오 {plan.limitValue}개 이하</span>
                      </PlanSelectFeatureItem>
                    )}
                    {plan.detailDescription && (
                      <PlanSelectDetailDesc>{plan.detailDescription}</PlanSelectDetailDesc>
                    )}
                  </PlanSelectFeatureList>
                )}

                <PlanSelectButtonWrapper>
                  <PlanSelectButton
                    disabled={isPlanProcessing}
                    $isHighlight={isFree && !isConsultation}
                    onClick={() => handlePlanSelect(plan)}
                  >
                    {isPlanProcessing ? (
                      <>
                        <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                        처리 중...
                      </>
                    ) : isConsultation ? (
                      '별도 협의 신청하기'
                    ) : isFree ? (
                      '무료로 시작하기'
                    ) : (
                      <>
                        <CreditCard size={16} />
                        시작하기
                      </>
                    )}
                  </PlanSelectButton>
                </PlanSelectButtonWrapper>
              </PlanSelectCard>
            );
          })}
        </PlanSelectGrid>
      )}

    </Section>
    );
  };

  useEffect(() => {
    
    setHeaderTitle('새 회사 등록');
    clearBackNavigation();
    setBackNavigation('회사 관리', '/space-management');
  }, []);

  return (
    <>
    <ContainerWrapper>
      <Container>
        {/* 히어로 섹션 */}
        <HeroSection>
          <HeroBackground>
            <HeroGradient />
            <HeroPattern />
          </HeroBackground>
          <HeroContent>
            <HeroIconWrapper>
              <Plus size={32} />
            </HeroIconWrapper>
            <HeroTextContent>
              <HeroTitle>새 회사 등록</HeroTitle>
              <HeroDescription>
                새로운 회사를 등록하고 비즈니스에 맞는 스페이스를 시작하세요.
                <br />
                등록 완료 후 마스터 권한을 받게 됩니다.
              </HeroDescription>
            </HeroTextContent>
          </HeroContent>
        </HeroSection>

        {/* 스텝 인디케이터 */}
        <StepIndicatorWrapper>
          <StepIndicator>
            {(() => {
              const steps = [
                { step: 1, label: '서비스 선택', sublabel: '사용할 서비스' },
                { step: 2, label: '유형 선택', sublabel: '창업자 유형' },
                { step: 3, label: '정보 입력', sublabel: '회사 정보' },
                ...(formData.serviceType === 'INVESTOR'
                  ? [{ step: 4, label: '플랜 선택', sublabel: '이용 플랜' }]
                  : []),
              ];
              return steps.map((item, index) => (
                <React.Fragment key={item.step}>
                  <StepItemNew 
                    $active={getCurrentStepNumber() >= item.step} 
                    $completed={getCurrentStepNumber() > item.step}
                  >
                    <StepNumberNew 
                      $active={getCurrentStepNumber() >= item.step} 
                      $completed={getCurrentStepNumber() > item.step}
                    >
                      {getCurrentStepNumber() > item.step ? <Check size={16} /> : item.step}
                    </StepNumberNew>
                    <StepInfoNew>
                      <StepLabelNew $active={getCurrentStepNumber() >= item.step}>
                        {item.label}
                      </StepLabelNew>
                      <StepSublabelNew>{item.sublabel}</StepSublabelNew>
                    </StepInfoNew>
                  </StepItemNew>
                  {index < steps.length - 1 && (
                    <StepConnector $completed={getCurrentStepNumber() > item.step} />
                  )}
                </React.Fragment>
              ));
            })()}
          </StepIndicator>
          {currentStep !== 'SERVICE_SELECT' && currentStep !== 'PLAN_SELECT' && (
            <BackButtonNew onClick={handleBack}>
              <ChevronLeft size={18} />
              이전 단계
            </BackButtonNew>
          )}
        </StepIndicatorWrapper>

        <ContentCard>
          <ContentHeader>
            <ContentTitleWrapper>
              <ContentIconWrapper>
                {currentStep === 'SERVICE_SELECT' && <LayoutGrid size={24} />}
                {currentStep === 'FOUNDER_TYPE' && <User size={24} />}
                {(currentStep === 'FORM_INPUT' || currentStep === 'BUSINESS_UPLOAD' || currentStep === 'VERIFICATION') && <Building2 size={24} />}
              </ContentIconWrapper>
              <ContentTitle>{getStepTitle()}</ContentTitle>
            </ContentTitleWrapper>
          </ContentHeader>
          
          {renderStepContent()}
        </ContentCard>
        
        {/* 모달들 */}
        {showModal && modalType === 'JOIN_REQUEST' && (
          <Modal>
            <ModalContent>
              <ModalTitle>가입 신청</ModalTitle>
              <ModalText>
                이미 등록된 회사입니다.<br />
                회사 마스터에게 가입 신청을 하시겠습니까?
              </ModalText>
              <ModalButtons>
                <Button $variant="outline" onClick={() => setShowModal(false)}>
                  취소
                </Button>
                <Button $variant="fill" onClick={handleFinalSubmit}>
                  가입 신청
                </Button>
              </ModalButtons>
            </ModalContent>
          </Modal>
        )}
        
        {showModal && modalType === 'MASTER_MATCH' && (
          <Modal>
            <ModalContent>
              <ModalTitle>회사 연결</ModalTitle>
              <ModalText>
                등록된 회사이지만 활성 사용자가 없습니다.<br />
                이 회사를 본인 계정과 연결하시겠습니까?
              </ModalText>
              <ModalButtons>
                <Button $variant="outline" onClick={() => setShowModal(false)}>
                  취소
                </Button>
                <Button $variant="fill" onClick={handleFinalSubmit}>
                  회사 연결
                </Button>
              </ModalButtons>
            </ModalContent>
          </Modal>
        )}
      </Container>
    </ContainerWrapper>

    {/* 도입 문의 모달 */}
    {consultationModal.isOpen && (
      <PlanConsultationOverlay onClick={() => setConsultationModal({ isOpen: false, productId: null, planName: '' })}>
        <PlanConsultationCard onClick={(e) => e.stopPropagation()}>
          <PlanConsultationHeader>
            <PlanConsultationHeaderLeft>
              <PlanConsultationIconBadge>
                <MessageSquare size={20} />
              </PlanConsultationIconBadge>
              <div>
                <PlanConsultationTitle>별도 협의 문의</PlanConsultationTitle>
                <PlanConsultationPlanTag>{consultationModal.planName} 플랜</PlanConsultationPlanTag>
              </div>
            </PlanConsultationHeaderLeft>
            <PlanConsultationCloseButton
              onClick={() => setConsultationModal({ isOpen: false, productId: null, planName: '' })}
            >
              ✕
            </PlanConsultationCloseButton>
          </PlanConsultationHeader>

          <PlanConsultationBody>
            <PlanConsultationDesc>
              <b>{consultationModal.planName}</b> 플랜은 별도 협의가 필요한 맞춤형 상품입니다.<br />
              아래에 문의 내용을 남겨주시면 담당자가 빠르게 연락드리겠습니다.
            </PlanConsultationDesc>
            <PlanConsultationFormGroup>
              <PlanConsultationLabel>
                문의 내용 <PlanConsultationOptional>(선택)</PlanConsultationOptional>
              </PlanConsultationLabel>
              <PlanConsultationTextarea
                value={consultationMessage}
                onChange={(e) => setConsultationMessage(e.target.value)}
                placeholder="도입 규모, 요구사항, 희망 일정 등 자유롭게 작성해주세요."
                maxLength={500}
                rows={5}
              />
              <PlanConsultationCharCount>{consultationMessage.length} / 500</PlanConsultationCharCount>
            </PlanConsultationFormGroup>
          </PlanConsultationBody>

          <PlanConsultationFooter>
            <PlanConsultationCancelButton
              onClick={() => setConsultationModal({ isOpen: false, productId: null, planName: '' })}
            >
              취소
            </PlanConsultationCancelButton>
            <PlanConsultationSubmitButton
              onClick={handleSubmitConsultation}
              disabled={isSubmittingConsultation}
            >
              {isSubmittingConsultation ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                '문의 보내기'
              )}
            </PlanConsultationSubmitButton>
          </PlanConsultationFooter>
        </PlanConsultationCard>
      </PlanConsultationOverlay>
    )}
    </>
  );
};

// ============================================
// Animations
// ============================================

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// ============================================
// Container & Layout
// ============================================

const ContainerWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  background: ${semanticColors.background.primary};
  padding: 1.5rem;
`;

const Container = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
`;

// ============================================
// Hero Section
// ============================================

const HeroSection = styled.div`
  position: relative;
  margin-bottom: 2rem;
  border-radius: 24px;
  overflow: hidden;
  animation: ${fadeInUp} 0.6s ease-out;
`;

const HeroBackground = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #059669 0%, #10b981 50%, #34d399 100%);
`;

const HeroGradient = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 30% 20%, rgba(255,255,255,0.15) 0%, transparent 50%),
              radial-gradient(circle at 70% 80%, rgba(255,255,255,0.1) 0%, transparent 40%);
`;

const HeroPattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.1;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
`;

const HeroContent = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 2rem;
  
  @media (max-width: 640px) {
    flex-direction: column;
    text-align: center;
  }
`;

const HeroIconWrapper = styled.div`
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.2);
  border-radius: 16px;
  color: #fff;
  flex-shrink: 0;
`;

const HeroTextContent = styled.div`
  flex: 1;
`;

const HeroTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.5rem;
`;

const HeroDescription = styled.p`
  ${typography.paragraph.p2.default}
  color: rgba(255,255,255,0.85);
  line-height: 1.6;
`;

// ============================================
// Step Indicator
// ============================================

const StepIndicatorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 16px;
  margin-bottom: 2rem;
  animation: ${fadeInUp} 0.6s ease-out 0.1s both;
`;

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
`;

const StepItemNew = styled.div<{ $active: boolean; $completed: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  opacity: ${props => (props.$active || props.$completed) ? 1 : 0.4};
  transition: opacity 0.3s ease;
`;

const StepNumberNew = styled.div<{ $active: boolean; $completed: boolean }>`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s ease;
  
  ${props => props.$completed && `
    background: ${semanticColors.background.success.primary};
    color: #fff;
  `}
  
  ${props => props.$active && !props.$completed && `
    background: linear-gradient(135deg, #059669 0%, #10b981 100%);
    color: #fff;
  `}
  
  ${props => !props.$active && !props.$completed && `
    background: ${semanticColors.background.tertiary};
    color: ${semanticColors.text.tertiary};
  `}
`;

const StepInfoNew = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  
  @media (max-width: 640px) {
    display: none;
  }
`;

const StepLabelNew = styled.span<{ $active: boolean }>`
  ${typography.subtitle.subtitle2}
  color: ${props => props.$active ? semanticColors.text.primary : semanticColors.text.tertiary};
`;

const StepSublabelNew = styled.span`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
`;

const StepConnector = styled.div<{ $completed: boolean }>`
  width: 60px;
  height: 2px;
  background: ${props => props.$completed
    ? semanticColors.background.success.primary
    : semanticColors.border.primary};
  transition: background 0.3s ease;
  
  @media (max-width: 768px) {
    width: 30px;
  }
`;

const BackButtonNew = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  align-self: flex-start;
  padding: 0.625rem 1rem;
  background: ${semanticColors.background.primary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 10px;
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.primaryHover};
    border-color: ${semanticColors.border.secondary};
    color: ${semanticColors.text.primary};
  }
`;

// Legacy step styles (kept for compatibility)
const Header = styled.div`
  display: none;
`;

const BackButton = styled.button<{ disabled?: boolean }>`
  display: none;
`;

const StepProgressContainer = styled.div`
  display: none;
`;

const StepProgressBar = styled.div`
  display: none;
`;

const StepItem = styled.div<{ $isActive: boolean; $isCurrent: boolean }>`
  display: none;
`;

const StepCircle = styled.div<{ $isActive: boolean; $isCurrent: boolean }>`
  display: none;
`;

const StepLabel = styled.span<{ $isActive: boolean }>`
  display: none;
`;

const StepLine = styled.div<{ $isActive: boolean }>`
  display: none;
`;

const ContentCard = styled.div`
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 20px;
  padding: 2rem;
  animation: ${fadeInUp} 0.6s ease-out 0.2s both;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 16px;
  }
`;

const ContentHeader = styled.div`
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid ${semanticColors.border.primary};
`;

const ContentTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
`;

const ContentIconWrapper = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  border-radius: 12px;
  color: #fff;
`;

const ContentTitle = styled.h3`
  ${typography.heading.h4}
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const SectionTitle = styled.h5`
  ${typography.heading.h5}
  color: ${semanticColors.text.primary};
  margin: 0;
  text-align: center;
`;

const SectionDescription = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  margin: 0;
  text-align: center;
  line-height: 1.6;
  padding: 0 1rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

// 서비스 선택 스타일 (3열 그리드, 모바일에서는 1열)
const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;
  margin-top: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div<{ $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.5rem;
  background: ${props => props.$disabled ? semanticColors.background.secondary : semanticColors.background.primary};
  border: 2px solid ${semanticColors.border.primary};
  border-radius: 16px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.25s ease;
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.4s ease-out;
  opacity: ${props => props.$disabled ? 0.6 : 1};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #059669 0%, #10b981 100%);
    opacity: 0;
    transition: opacity 0.25s ease;
  }
  
  &:hover {
    ${props => !props.$disabled && `
      border-color: #10b981;
      box-shadow: 0 12px 32px rgba(16, 185, 129, 0.15);
      transform: translateY(-6px);
      
      &::before {
        opacity: 1;
      }
    `}
  }
  
  &:active {
    ${props => !props.$disabled && `
      transform: translateY(-3px);
    `}
  }
`;

const PreparingBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 0.25rem 0.75rem;
  background: ${semanticColors.background.warning.secondary};
  color: ${semanticColors.text.onWarning.primary};
  border-radius: 100px;
  ${typography.caption.caption2};
  font-weight: 700;
  z-index: 1;
`;

const ServiceHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const ServiceIcon = styled.div`
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.15) 100%);
  border-radius: 14px;
  flex-shrink: 0;
  color: #059669;
  transition: all 0.25s ease;
  
  ${ServiceCard}:hover & {
    background: linear-gradient(135deg, #059669 0%, #10b981 100%);
    color: #fff;
    transform: scale(1.05);
  }
`;

const ServiceInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  flex: 1;
`;

const ServiceName = styled.h6`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  margin: 0;
  font-weight: 700;
`;

const ServiceDescription = styled.p`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.secondary};
  margin: 0;
  line-height: 1.5;
`;

// 창업자 타입 선택 스타일
const FounderTypeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-top: 1rem;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FounderTypeCard = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
  padding: 2.5rem 2rem;
  background: ${props => props.disabled ? semanticColors.background.disabled : semanticColors.background.primary};
  border: 2px solid ${props => props.disabled ? semanticColors.border.secondary : semanticColors.border.primary};
  border-radius: 20px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.25s ease;
  text-align: center;
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.4s ease-out;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #059669 0%, #10b981 100%);
    opacity: 0;
    transition: opacity 0.25s ease;
  }
  
  &:hover {
    ${props => !props.disabled && `
      border-color: #10b981;
      box-shadow: 0 12px 32px rgba(16, 185, 129, 0.15);
      transform: translateY(-6px);
      
      &::before {
        opacity: 1;
      }
    `}
  }
  
  &:active {
    ${props => !props.disabled && `
      transform: translateY(-3px);
    `}
  }
`;

const FounderTypeIconWrapper = styled.div<{ $disabled?: boolean }>`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$disabled 
    ? semanticColors.background.disabled 
    : 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.15) 100%)'};
  border-radius: 50%;
  color: ${props => props.$disabled 
    ? semanticColors.text.disabled 
    : '#059669'};
  transition: all 0.25s ease;
  
  ${FounderTypeCard}:hover & {
    ${props => !props.$disabled && `
      background: linear-gradient(135deg, #059669 0%, #10b981 100%);
      color: #fff;
      transform: scale(1.05);
    `}
  }
`;

const FounderTypeIcon = styled.div<{ disabled?: boolean }>`
  font-size: 40px;
  opacity: ${props => props.disabled ? 0.5 : 1};
`;

const FounderTypeInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FounderTypeName = styled.h4<{ disabled?: boolean }>`
  ${typography.heading.h4}
  color: ${props => props.disabled ? semanticColors.text.tertiary : semanticColors.text.primary};
  margin: 0;
  font-weight: 700;
`;

const FounderTypeDesc = styled.p<{ disabled?: boolean }>`
  ${typography.paragraph.p2.default}
  color: ${props => props.disabled ? semanticColors.text.tertiary : semanticColors.text.secondary};
  margin: 0;
  line-height: 1.5;
`;

const DisabledText = styled.span`
  display: block;
  margin-top: 0.5rem;
  padding: 0.375rem 0.75rem;
  background: ${semanticColors.background.danger.secondary};
  border-radius: 6px;
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.onDanger.secondary};
`;

// 검증된 회사 정보 카드
const VerifiedCompanyCard = styled.div`
  background: ${semanticColors.background.success.secondary};
  border: 1px solid ${semanticColors.border.success};
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  animation: ${fadeIn} 0.4s ease-out;
`;

const VerifiedCompanyHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${semanticColors.border.success};
`;

const VerifiedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: ${semanticColors.background.success.primary};
  color: #fff;
  border-radius: 100px;
  ${typography.caption.caption1.medium}
`;

const VerifiedCompanyName = styled.h4`
  ${typography.heading.h5}
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const VerifiedCompanyInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const VerifiedInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 10px;
`;

const VerifiedInfoLabel = styled.span`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
`;

const VerifiedInfoValue = styled.span`
  ${typography.paragraph.p2.medium}
  color: ${semanticColors.text.primary};
`;

// 폼 섹션 스타일
const FormSection = styled.div`
  background: ${semanticColors.background.primary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.25rem;
`;

const FormSectionTitle = styled.h5`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  margin: 0 0 0.25rem 0;
  
  &::before {
    content: '';
    width: 4px;
    height: 20px;
    background: linear-gradient(135deg, #059669 0%, #10b981 100%);
    border-radius: 2px;
  }
`;

const FormSectionDesc = styled.p`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
  margin: 0 0 1.25rem 0;
  padding-left: calc(4px + 0.5rem);
`;

// 라디오 카드 스타일
const RadioCardGroup = styled.div`
  display: flex;
  gap: 12px;
  
  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const RadioCard = styled.div<{ $isSelected: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: ${props => props.$isSelected 
    ? `${semanticColors.background.brand}08` 
    : semanticColors.background.secondary};
  border: 2px solid ${props => props.$isSelected 
    ? semanticColors.background.brand 
    : semanticColors.border.primary};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.$isSelected 
      ? semanticColors.background.brand 
      : semanticColors.border.secondary};
  }
`;

const RadioCardIcon = styled.div<{ $isSelected: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$isSelected 
    ? semanticColors.background.brand 
    : semanticColors.background.secondary};
  border: 2px solid ${props => props.$isSelected 
    ? semanticColors.background.brand 
    : semanticColors.border.primary};
  color: ${semanticColors.text.white};
  transition: all 0.2s ease;
`;

const RadioCardText = styled.span`
  ${typography.paragraph.p2.medium}
  color: ${semanticColors.text.primary};
`;

// 대표자 상태 카드 스타일
const RepresentativeStatusCard = styled.div<{ $isRepresentative: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  background: ${props => props.$isRepresentative 
    ? semanticColors.background.success.secondary 
    : semanticColors.background.warning.secondary};
  border: 1px solid ${props => props.$isRepresentative 
    ? semanticColors.background.success.primary 
    : semanticColors.background.warning.primary};
  border-radius: 12px;
  margin-bottom: 16px;
`;

const RepresentativeStatusIcon = styled.div<{ $isRepresentative: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$isRepresentative 
    ? semanticColors.background.success.primary 
    : semanticColors.background.warning.primary};
  color: ${semanticColors.text.white};
  flex-shrink: 0;
`;

const RepresentativeStatusContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RepresentativeStatusTitle = styled.h6`
  ${typography.subtitle.subtitle2}
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const RepresentativeStatusDesc = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  margin: 0;
  line-height: 1.5;
`;

// 폼 입력 스타일
const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  grid-column: ${props => props.$fullWidth ? '1 / -1' : 'auto'};
`;

const Label = styled.label`
  ${typography.paragraph.p1.default}
  color: ${semanticColors.text.primary};
  font-weight: 600;
`;

const HelpText = styled.span`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
`;

const ErrorMessage = styled.span`
  ${typography.caption.caption2}
  color: ${semanticColors.background.danger.primary};
`;

const CodeInputGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const CheckCodeButton = styled.button<{ $isChecked: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 10px;
  background: ${props => 
    props.$isChecked 
      ? semanticColors.background.success.primary 
      : 'linear-gradient(135deg, #059669 0%, #10b981 100%)'
  };
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  min-width: 100px;
  height: 43px;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  
  input[type="radio"] {
    margin: 0;
  }
  
  span {
    ${typography.paragraph.p1.default}
    color: ${semanticColors.text.primary};
  }
`;

// 파일 업로드 스타일
const FileUploadArea = styled.div`
  margin-top: 16px;
`;

const FileUploadBox = styled.div<{ $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px 20px;
  border: 2px dashed ${semanticColors.border.primary};
  border-radius: 12px;
  background: ${semanticColors.background.secondary};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  opacity: ${props => props.$disabled ? 0.6 : 1};
  
  &:hover {
    ${props => !props.$disabled && `
      border-color: ${semanticColors.background.brand};
      background: ${semanticColors.background.primary};
    `}
  }
`;

const FileSelectButton = styled.span<{ $disabled?: boolean }>`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.background.brand};
  font-weight: 600;
  padding: 8px 16px;
  border: 1px solid ${semanticColors.background.brand};
  border-radius: 8px;
  transition: all 0.2s ease;
  pointer-events: none;
  
  ${props => !props.$disabled && `
    &:hover {
      background: ${semanticColors.background.brand};
      color: ${semanticColors.text.white};
    }
  `}
`;

const FileUploadIcon = styled.div`
  font-size: 40px;
`;

const FileUploadText = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.primary};
  margin: 0;
  text-align: center;
`;

const FileUploadHint = styled.p`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
  margin: 0;
`;

const LoadingMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  justify-content: center;
  margin-top: 1.25rem;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.15) 100%);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 12px;
  ${typography.paragraph.p2.default}
  color: #059669;
  font-weight: 500;
`;

const LoadingIcon = styled.span`
  font-size: 20px;
  animation: ${spin} 1s linear infinite;
`;

const LoadingIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #059669;
  animation: ${spin} 1s linear infinite;
`;

// AI 분석 및 사업자 정보 스타일
const VerificationSuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: ${semanticColors.background.success.secondary};
  border: 1px solid ${semanticColors.border.success};
  border-radius: 16px;
  margin-bottom: 1.5rem;
`;

const SuccessIcon = styled.div`
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.success.primary};
  border-radius: 50%;
  font-size: 28px;
`;

const SuccessIconWrapper = styled.div`
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.success.primary};
  border-radius: 50%;
  color: #fff;
`;

const SuccessMessage = styled.p`
  ${typography.paragraph.p1.default}
  color: ${semanticColors.text.primary};
  margin: 0;
  text-align: center;
  font-weight: 600;
`;

const BusinessInfoContainer = styled.div`
  background: ${semanticColors.background.primary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const BusinessInfoTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${semanticColors.border.primary};
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
`;

const CheckIcon = styled.span`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.success.primary};
  border-radius: 50%;
  color: #fff;
  font-size: 14px;
`;

const CheckIconWrapper = styled.div`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.success.primary};
  border-radius: 50%;
  color: #fff;
`;

const EditIcon = styled.span`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.warning.secondary};
  border-radius: 50%;
  font-size: 14px;
`;

const EditIconWrapper = styled.div`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.warning.secondary};
  border-radius: 50%;
  color: ${semanticColors.background.warning.primary};
`;

const BusinessInfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// 공통 스타일
const FeatureList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${semanticColors.border.tertiary};
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: ${semanticColors.background.secondary};
  border-radius: 8px;
  transition: all 0.2s ease;
  
  ${ServiceCard}:hover & {
    background: rgba(16, 185, 129, 0.1);
  }
`;

const FeatureIcon = styled.span`
  ${typography.caption.caption2}
  color: #059669;
  font-weight: bold;
`;

const FeatureText = styled.span`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.secondary};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${semanticColors.border.primary};
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

// 모달 스타일
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease-out;
`;

const ModalContent = styled.div`
  background: ${semanticColors.background.primary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 20px;
  padding: 2rem;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  animation: ${fadeInUp} 0.3s ease-out;
`;

const ModalTitle = styled.h3`
  ${typography.heading.h4}
  color: ${semanticColors.text.primary};
  margin: 0 0 0.75rem 0;
  text-align: center;
`;

const ModalText = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  margin: 0 0 1.5rem 0;
  text-align: center;
  line-height: 1.6;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
`;

// 기존 회사 정보 표시 스타일
const ExistingCompanyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem 1.5rem;
  background: linear-gradient(135deg, ${semanticColors.background.tertiary} 0%, ${semanticColors.background.secondary} 100%);
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 16px;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const CompanyFoundIcon = styled.div`
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  border-radius: 50%;
  font-size: 36px;
  margin-bottom: 0.5rem;
`;

const CompanyFoundIconWrapper = styled.div`
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  border-radius: 50%;
  color: #fff;
  margin-bottom: 0.5rem;
`;

const CompanyFoundTitle = styled.h5`
  ${typography.heading.h5}
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const CompanyFoundDescription = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  margin: 0;
  line-height: 1.6;
  max-width: 400px;
`;

const ExistingCompanyInfoCard = styled.div`
  background: ${semanticColors.background.primary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const CompanyInfoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${semanticColors.border.primary};
`;

const CompanyInfoIcon = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.tertiary};
  border-radius: 10px;
  font-size: 20px;
`;

const CompanyInfoIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.tertiary};
  border-radius: 10px;
  color: ${semanticColors.text.secondary};
`;

const CompanyInfoTitle = styled.h4`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  margin: 0;
  flex: 1;
`;

const CompanyStatusBadge = styled.div<{ $status: 'active' | 'pending' }>`
  padding: 0.375rem 0.875rem;
  border-radius: 100px;
  ${typography.caption.caption1.medium}
  color: #fff;
  background: ${props => 
    props.$status === 'active' 
      ? semanticColors.background.success.primary 
      : semanticColors.background.warning.primary
  };
`;

const CompanyInfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
`;

const CompanyInfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid ${semanticColors.border.tertiary};
  
  &:last-child {
    border-bottom: none;
  }
`;

const CompanyInfoLabel = styled.div`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  font-weight: 600;
  min-width: 120px;
`;

const CompanyInfoValue = styled.div`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.primary};
  flex: 1;
  text-align: right;
`;

// 마스터 정보 스타일
const MasterUserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-end;
`;

const MasterUserAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${semanticColors.background.brand};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
`;

const MasterUserDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: right;
`;

const MasterUserName = styled.div`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.primary};
  font-weight: 600;
`;

const MasterUserEmail = styled.div`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
`;

// 마스터 없음 정보 스타일
const NoMasterInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
`;

const NoMasterIcon = styled.div`
  font-size: 16px;
`;

const NoMasterIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${semanticColors.text.tertiary};
`;

const NoMasterText = styled.div`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.tertiary};
  font-style: italic;
`;

// 안내 메시지 스타일
const JoinRequestNotice = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.25rem;
  background: ${semanticColors.background.info.secondary};
  border: 1px solid ${semanticColors.border.info};
  border-radius: 12px;
  margin-bottom: 1.5rem;
`;

const MasterRegistrationNotice = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.25rem;
  background: ${semanticColors.background.warning.secondary};
  border: 1px solid ${semanticColors.border.warning};
  border-radius: 12px;
  margin-bottom: 1.5rem;
`;

const NoticeIcon = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  font-size: 18px;
  flex-shrink: 0;
`;

const NoticeIconWrapper = styled.div<{ $type: 'info' | 'warning' }>`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$type === 'info' 
    ? semanticColors.background.info.primary
    : semanticColors.background.warning.primary};
  border-radius: 10px;
  color: #fff;
  flex-shrink: 0;
`;

const NoticeText = styled.div`
  flex: 1;
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.primary};
  line-height: 1.6;
  
  strong {
    display: block;
    font-weight: 700;
    color: ${semanticColors.text.primary};
    margin-bottom: 0.25rem;
  }
`;

// 가입 신청 이메일 섹션 스타일
const JoinRequestEmailSection = styled.div`
  background: ${semanticColors.background.primary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
`;

const EmailSectionTitle = styled.h4`
  ${typography.heading.h6}
  color: ${semanticColors.text.primary};
  margin: 0 0 12px 0;
  font-weight: 600;
`;

const EmailInputGroup = styled.div`
  margin-bottom: 8px;
`;

const EmailHelpText = styled.p`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
  margin: 0;
  margin-top: 8px;
  line-height: 1.4;
`;

// 마스터 등록 폼 섹션 스타일
const MasterRegistrationFormSection = styled.div`
  background: ${semanticColors.background.primary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
`;

const MasterFormTitle = styled.h4`
  ${typography.heading.h6}
  color: ${semanticColors.text.primary};
  margin: 0 0 16px 0;
  font-weight: 600;
`;

const MasterFormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MasterFormGroup = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  grid-column: ${props => props.$fullWidth ? '1 / -1' : 'auto'};
`;

// ============================================
// Plan Select Step Styles
// ============================================

const PlanGuideBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.04) 100%);
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 12px;
  margin-bottom: 1.5rem;
`;

const PlanGuideIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: white;
  flex-shrink: 0;
`;

const PlanGuideContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const PlanGuideTitle = styled.div`
  ${typography.paragraph.p1.default};
  font-weight: 700;
  color: ${semanticColors.text.primary};
`;

const PlanGuideDescription = styled.div`
  ${typography.paragraph.p2.default};
  color: ${semanticColors.text.secondary};
  line-height: 1.5;
`;

const PlanLoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 2rem;
  color: ${semanticColors.text.secondary};
  ${typography.paragraph.p1.default};

  svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const PlanEmptyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 3rem 2rem;
  text-align: center;
  color: ${semanticColors.text.secondary};
  ${typography.paragraph.p1.default};
`;

const planResultFadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PlanResultContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1rem;
  padding: 3.5rem 1.5rem 3rem;
  animation: ${planResultFadeIn} 0.4s ease-out;
`;

const PlanResultIconWrapper = styled.div<{ $variant: 'success' | 'refund' | 'info' }>`
  width: 88px;
  height: 88px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
  background: ${({ $variant }) =>
    $variant === 'refund'
      ? colorTokens.blue[100]
      : $variant === 'info'
        ? colorTokens.gray[100]
        : colorTokens.green[100]};
  color: ${({ $variant }) =>
    $variant === 'refund'
      ? colorTokens.blue[500]
      : $variant === 'info'
        ? colorTokens.brand[600]
        : colorTokens.green[500]};
`;

const PlanResultTitle = styled.h2`
  ${typography.heading.h4};
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const PlanResultPlanName = styled.span`
  ${typography.paragraph.p1.medium};
  color: ${semanticColors.text.secondary};
  background: ${colorTokens.gray[100]};
  padding: 0.25rem 0.875rem;
  border-radius: 20px;
`;

const PlanResultAmountBadge = styled.div<{ $variant: 'charge' | 'refund' }>`
  ${typography.subtitle.subtitle2};
  padding: 0.5rem 1.25rem;
  border-radius: 8px;
  background: ${({ $variant }) =>
    $variant === 'refund' ? colorTokens.blue[100] : colorTokens.green[100]};
  color: ${({ $variant }) =>
    $variant === 'refund' ? colorTokens.blue[600] : colorTokens.green[600]};
`;

const PlanResultDescription = styled.p`
  ${typography.paragraph.p1.default};
  color: ${semanticColors.text.secondary};
  line-height: 1.7;
  margin: 0.5rem 0 0;
  max-width: 420px;
`;

const PlanResultMessage = styled.p`
  ${typography.paragraph.p2.default};
  color: ${semanticColors.text.tertiary};
  margin: 0;
  max-width: 420px;
  line-height: 1.6;
  white-space: pre-line;
`;

const PlanResultConfirmButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.25rem;
  padding: 0.875rem 2.25rem;
  border: none;
  border-radius: 10px;
  background: ${colorTokens.brand[600]};
  color: white;
  ${typography.subtitle.subtitle2};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${colorTokens.brand[700]};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const PlanSelectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.25rem;
`;

const PlanSelectCard = styled.div<{ $isHighlight?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 1.75rem;
  background: ${semanticColors.background.secondary};
  border: 2px solid ${props => props.$isHighlight ? '#10b981' : semanticColors.border.primary};
  border-radius: 16px;
  transition: all 0.25s ease;
  overflow: hidden;

  ${props => props.$isHighlight && `
    box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.1), 0 8px 24px rgba(16, 185, 129, 0.12);
  `}

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.$isHighlight
      ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)'
      : 'transparent'};
  }
`;

const PlanFreeBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 0.25rem 0.75rem;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: white;
  border-radius: 100px;
  ${typography.caption.caption2};
  font-weight: 700;
`;

const PlanConsultationBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 0.25rem 0.75rem;
  background: ${semanticColors.background.warning.primary};
  color: white;
  border-radius: 100px;
  ${typography.caption.caption2};
  font-weight: 700;
`;

const PlanSelectHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
`;

const PlanSelectName = styled.div`
  ${typography.heading.h5};
  color: ${semanticColors.text.primary};
  font-weight: 700;
`;

const PlanSelectSubName = styled.div`
  ${typography.paragraph.p2.default};
  color: ${semanticColors.text.tertiary};
`;

const PlanSelectDescription = styled.div`
  ${typography.paragraph.p2.default};
  color: ${semanticColors.text.secondary};
  line-height: 1.5;
  min-height: 2.5em;
`;

const PlanSelectPriceWrapper = styled.div`
  margin-top: 0.75rem;
`;

const PlanSelectPrice = styled.div`
  ${typography.heading.h4};
  color: #059669;
  font-weight: 800;
`;

const PlanSelectPriceContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
`;

const PlanSelectPriceNumber = styled.span`
  ${typography.heading.h4};
  color: ${semanticColors.text.primary};
  font-weight: 800;
`;

const PlanSelectPriceUnit = styled.span`
  ${typography.paragraph.p2.default};
  color: ${semanticColors.text.tertiary};
`;

const PlanSelectFeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding: 1rem 0;
  border-top: 1px solid ${semanticColors.border.secondary};
  margin-bottom: auto;
`;

const PlanSelectFeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  ${typography.paragraph.p2.default};
  color: ${semanticColors.text.secondary};
`;

const PlanSelectFeatureIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
  flex-shrink: 0;
`;

const PlanSelectDetailDesc = styled.div`
  ${typography.paragraph.p2.default};
  color: ${semanticColors.text.tertiary};
  line-height: 1.6;
  white-space: pre-line;
`;

const PlanSelectButtonWrapper = styled.div`
  margin-top: 1.25rem;
`;

const PlanSelectButton = styled.button<{ $isHighlight?: boolean }>`
  width: 100%;
  padding: 0.875rem 1.5rem;
  border-radius: 10px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  ${typography.paragraph.p1.default};

  background: ${props => props.$isHighlight
    ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)'
    : semanticColors.background.primary};
  color: ${props => props.$isHighlight ? 'white' : semanticColors.text.primary};
  border: ${props => props.$isHighlight ? 'none' : `1px solid ${semanticColors.border.primary}`};

  &:hover:not(:disabled) {
    ${props => props.$isHighlight
      ? 'opacity: 0.9; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);'
      : `background: ${semanticColors.background.primaryHover};`}
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// ─── 도입 문의 모달 스타일 ──────────────────────────────────────────────────────
const PlanConsultationOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 20px;
`;

const PlanConsultationCard = styled.div`
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  overflow: hidden;
`;

const PlanConsultationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${semanticColors.border.primary};
`;

const PlanConsultationHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PlanConsultationIconBadge = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${semanticColors.background.primaryHover};
  color: ${semanticColors.text.brand};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const PlanConsultationTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${semanticColors.text.primary};
`;

const PlanConsultationPlanTag = styled.div`
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.brand};
  margin-top: 2px;
`;

const PlanConsultationCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 18px;
  color: ${semanticColors.text.secondary};
  padding: 4px;
  line-height: 1;
  &:hover { color: ${semanticColors.text.primary}; }
`;

const PlanConsultationBody = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PlanConsultationDesc = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: ${semanticColors.text.secondary};
  line-height: 1.6;
  margin: 0;
`;

const PlanConsultationFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const PlanConsultationLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${semanticColors.text.primary};
`;

const PlanConsultationOptional = styled.span`
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.tertiary};
  margin-left: 4px;
`;

const PlanConsultationTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 8px;
  resize: vertical;
  font-size: 14px;
  font-weight: 400;
  color: ${semanticColors.text.primary};
  line-height: 1.6;
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.15s;
  &:focus { border-color: ${semanticColors.border.secondary}; }
  &::placeholder { color: ${semanticColors.text.tertiary}; }
`;

const PlanConsultationCharCount = styled.div`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  text-align: right;
`;

const PlanConsultationFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid ${semanticColors.border.primary};
  background: ${semanticColors.background.secondary};
`;

const PlanConsultationCancelButton = styled.button`
  padding: 10px 20px;
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 8px;
  background: #fff;
  font-size: 14px;
  font-weight: 500;
  color: ${semanticColors.text.secondary};
  cursor: pointer;
  &:hover { background: ${semanticColors.background.primaryHover}; }
`;

const PlanConsultationSubmitButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  background: ${semanticColors.text.brand};
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover:not(:disabled) { opacity: 0.85; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export default NewCompanyRegistration;
